import type {
  CandidatDoublon,
  ChampFicheContactModifiable,
  ContactExtrait,
  EntrepriseResume,
  Fiche,
  FicheContact,
  NoteRecall,
} from '@ocerm/shared';
import {
  type ChangeEvent,
  type ClipboardEvent,
  type FormEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  type ApercuImport,
  apercuImportVcard,
  type ContactResume,
  type CoordonneesContact,
  choisirContactsTelephone,
  compteurNotifications,
  confirmerImportVcard,
  ErreurApi,
  envoyerChat,
  envoyerMention,
  executerAction,
  extraireCarte,
  lireContact,
  listerMembres,
  listerNotifications,
  listerTagsDispo,
  type Membre,
  type MessageChat,
  marquerNotificationLue,
  marquerToutesNotificationsLues,
  moi,
  type Notification as NotificationOcerm,
  pickerContactsDispo,
  proposerAjoutNote,
  proposerAjoutTag,
  proposerFiche,
  proposerMajChampContact,
  proposerModifNote,
  proposerNoteMention,
  proposerRattachementNote,
  proposerRetraitTag,
  proposerSuppressionContact,
  proposerSuppressionNote,
  type ResultatImport,
  rechercher,
  type TagDisponible,
  testerPushMoi,
  transcrire,
} from './api.ts';
import {
  deconnecter,
  estConnecte,
  finaliserConnexion,
  oauthConfigure,
  restaurerSession,
  seConnecter,
} from './auth.ts';
import {
  detecterRequeteSlashFinale,
  normaliserTexteSaisie,
  textePourChampSaisie,
} from './composer-texte.ts';
import { compresserImage } from './compress.ts';
import { lireFile, mettreEnFile, retirerDeFile } from './file-attente.ts';
import {
  IconeCamera,
  IconeCloche,
  IconeClocheOff,
  IconeCompte,
  IconeContacts,
  IconeEnvoi,
  IconeImage,
  IconeInstaller,
  IconeMicro,
  IconeStop,
} from './icons.tsx';
import { estMoi } from './identite.ts';
import { IMPORT_VCARD_AIDE } from './import-aide.ts';
import { type ModeSelectionImport, selectionImportPour } from './import-selection.ts';
import { MajPwa } from './MajPwa.tsx';
import { Markdown } from './markdown.tsx';
import {
  activerNotifications,
  endpointPushActuel,
  etatAutorisation,
  notificationsSupportees,
} from './push.ts';
import { MESSAGE_CARTE_LUE } from './scan-carte-copy.ts';

let compteur = 0;
const id = () => `m${++compteur}`;
const TWENTY_URL = import.meta.env.VITE_TWENTY_URL ?? '';

function estIdentiteTechnique(p: string): boolean {
  const v = p.trim();
  return (
    /^(?:dev|device)-[a-z0-9_-]{8,}$/i.test(v) ||
    /^u_[a-f0-9]{16}$/i.test(v) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v)
  );
}

/** Affiche un propriétaire / auteur : « toi » pour cet appareil, un vrai label humain
 *  tel quel, et un libellé générique pour les anciennes identités techniques. */
function nomIdentite(p: string): string {
  const valeur = p.trim();
  if (estMoi(valeur)) return 'toi';
  if (!valeur || estIdentiteTechnique(valeur)) return 'un autre appareil';
  return valeur;
}

/** Date courte d'une note (ISO -> « 14/06 »). Vide si non datée/illisible. */
function dateNoteCourte(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/** Détecte une affirmation COURTE d'enregistrement (« enregistre », « oui », « ajoute-le »).
 *  Match sur le message ENTIER (pas un préfixe) : « ajoute une note » n'est pas capté,
 *  seules les confirmations brèves le sont, pour ne pas détourner une vraie demande. */
function estAffirmationEnregistrer(texte: string): boolean {
  const t = texte
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[!.…\s]+$/, '');
  return /^(oui|ok|d'accord|daccord|enregistre(-?le|-?la)?|enregistre[ -]?ce contact|ajoute(-?le|-?la)?|cree(-?le|-?la)?|cree la fiche|valide(-?le)?|vas-?y|go|c'est bon|parfait|confirme)$/.test(
    t,
  );
}

function texteDepuisSaisie(el: HTMLElement): string {
  return normaliserTexteSaisie(el.textContent ?? '');
}

function placerCurseurFin(el: HTMLElement) {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

type DocumentAvecCaret = Document & {
  caretRangeFromPoint?: (x: number, y: number) => Range | null;
  caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
};

function poserSelection(el: HTMLElement, range: Range): boolean {
  if (!el.contains(range.commonAncestorContainer)) return false;
  const selection = window.getSelection();
  if (!selection) return false;
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
}

function placerCurseurAuPointOuFin(el: HTMLElement, x: number, y: number) {
  const doc = document as DocumentAvecCaret;
  const rangeWebkit = doc.caretRangeFromPoint?.(x, y);
  if (rangeWebkit && poserSelection(el, rangeWebkit)) return;
  const position = doc.caretPositionFromPoint?.(x, y);
  if (position && el.contains(position.offsetNode)) {
    const range = document.createRange();
    range.setStart(position.offsetNode, position.offset);
    range.collapse(true);
    if (poserSelection(el, range)) return;
  }
  placerCurseurFin(el);
}

function capturerRestaurationScroll() {
  const fil = document.querySelector<HTMLElement>('.fil');
  const filTop = fil?.scrollTop;
  const x = window.scrollX;
  const y = window.scrollY;
  return () => {
    if (fil && filTop !== undefined && Math.abs(fil.scrollTop - filTop) > 1) {
      fil.scrollTop = filTop;
    }
    if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y);
  };
}

function focusSaisieSansScroll(el: HTMLElement, point?: { x: number; y: number }) {
  const restaurer = capturerRestaurationScroll();
  try {
    el.focus({ preventScroll: true });
  } catch {
    el.focus();
  }
  if (point) placerCurseurAuPointOuFin(el, point.x, point.y);
  else placerCurseurFin(el);
  requestAnimationFrame(restaurer);
  window.setTimeout(restaurer, 80);
  window.setTimeout(synchroniserViewportVisible, 80);
}

function synchroniserViewportVisible() {
  const viewport = window.visualViewport;
  const hauteur = Math.round(viewport?.height ?? window.innerHeight);
  document.documentElement.style.setProperty('--ocerm-viewport-height', `${hauteur}px`);
  // iOS Safari tente parfois de scroller le document racine au focus d'un champ
  // éditable. L'app est volontairement figée ; seul `.fil` doit défiler.
  if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
  const scrolling = document.scrollingElement;
  if (scrolling && scrolling.scrollTop !== 0) scrolling.scrollTop = 0;
}

type Item =
  | { id: string; role: 'user'; texte: string; vocal?: boolean; enAttente?: boolean }
  | { id: string; role: 'user'; imageUrl: string }
  | { id: string; role: 'assistant'; texte: string }
  | { id: string; role: 'assistant'; proposition: Proposition }
  | { id: string; role: 'assistant'; fiche: ContactExtrait }
  | {
      id: string;
      role: 'assistant';
      doublon: { contact: ContactExtrait; candidats: CandidatDoublon[] };
    }
  | { id: string; role: 'assistant'; contact: FicheContact }
  | { id: string; role: 'assistant'; liste: ContactResume[] }
  | { id: string; role: 'assistant'; entreprises: EntrepriseResume[] }
  | { id: string; role: 'assistant'; coordonnees: CoordonneesContact }
  | { id: string; role: 'assistant'; notesRecall: NoteRecall[] }
  | { id: string; role: 'systeme'; texte: string };

type PhasePanneauContact = 'ferme' | 'entrant' | 'ouvert' | 'sortant';

interface Proposition {
  description: string;
  jeton: string;
  outil?: string;
  contactId?: string;
}

const ACCUEIL: Item = {
  id: 'accueil',
  role: 'assistant',
  texte:
    "Bonjour 👋 Je suis l'assistant OcéRM. Demande-moi de **chercher**, **résumer**, **ajouter** ou **mettre à jour** un contact. Tu peux écrire, **photographier une carte** 📇, ou **dicter** 🎙️.",
};

export function App() {
  const [items, setItems] = useState<Item[]>([ACCUEIL]);
  const [contactsPanneau, setContactsPanneau] = useState<FicheContact[]>([]);
  const [contactPanneauId, setContactPanneauId] = useState<string | null>(null);
  const [contactPanneauPhase, setContactPanneauPhase] = useState<PhasePanneauContact>('ferme');
  const contactOuvert =
    contactsPanneau.find((contact) => contact.id === contactPanneauId) ??
    contactsPanneau[0] ??
    null;
  const [enCours, setEnCours] = useState(false);
  // Carte scannée affichée mais pas encore enregistrée. Si l'utilisateur tape une
  // affirmation courte (« enregistre », « oui »...) plutôt que de cliquer « Créer la
  // fiche », on crée le contact avec les données EXTRAITES (nom, adresse, tél, email)
  // au lieu d'envoyer au chat, qui ne les voit pas et redemanderait tout.
  const [ficheEnAttente, setFicheEnAttente] = useState<ContactExtrait | null>(null);
  const [connecte, setConnecte] = useState(estConnecte());
  // Prénom de l'utilisateur connecté (affiché dans le header à la place de l'icône).
  // Résolu via /me ; null si pas connecté ou non résolu (on retombe sur l'icône).
  const [prenom, setPrenom] = useState<string | null>(null);
  const finRef = useRef<HTMLDivElement>(null);
  const urlsBlob = useRef<Set<string>>(new Set());
  const fermeturePanneauRef = useRef<number | null>(null);
  // Garde anti-double-rejeu (#14) : l'event `online` ET le rejeu au montage peuvent
  // se déclencher quasi en même temps ; sans ce verrou, un message partirait 2 fois.
  const rejeuEnCours = useRef(false);

  // Au démarrage : finalise un retour de redirection OAuth, sinon restaure une session
  // (refresh_token ayant survécu au rechargement). Met à jour l'état connecté.
  useEffect(() => {
    void (async () => {
      const ok = (await finaliserConnexion()) || (await restaurerSession());
      if (ok) setConnecte(true);
    })();
  }, []);

  // Sur iPhone, le clavier réduit le VisualViewport sans forcément réduire le
  // layout viewport CSS. On pilote donc la hauteur de l'app depuis la zone
  // réellement visible pour que le composer reste collé au clavier.
  useEffect(() => {
    const actualiser = () => {
      requestAnimationFrame(synchroniserViewportVisible);
      window.setTimeout(synchroniserViewportVisible, 80);
    };
    actualiser();
    window.visualViewport?.addEventListener('resize', actualiser);
    window.visualViewport?.addEventListener('scroll', actualiser);
    window.addEventListener('resize', actualiser);
    window.addEventListener('orientationchange', actualiser);
    return () => {
      window.visualViewport?.removeEventListener('resize', actualiser);
      window.visualViewport?.removeEventListener('scroll', actualiser);
      window.removeEventListener('resize', actualiser);
      window.removeEventListener('orientationchange', actualiser);
    };
  }, []);

  // Prénom de l'utilisateur connecté (header). Récupéré sur connexion ; échec ->
  // on garde null (l'icône compte reste affichée). Dépend de `connecte` car la
  // restauration de session est asynchrone.
  useEffect(() => {
    if (!connecte) {
      setPrenom(null);
      return;
    }
    void moi()
      .then((m) => setPrenom(m?.nom?.trim().split(/\s+/)[0] ?? null))
      .catch(() => setPrenom(null));
  }, [connecte]);

  // Défile à chaque nouvel item. On scrolle UNIQUEMENT le conteneur de la
  // conversation (.fil), pas la page : scrollIntoView pouvait scroller le body
  // (mobile, clavier/100dvh) et faire « remonter » tout l'écran (header + fil).
  // biome-ignore lint/correctness/useExhaustiveDependencies: défile à chaque nouvel item
  useEffect(() => {
    const fil = finRef.current?.parentElement;
    if (fil) fil.scrollTo({ top: fil.scrollHeight, behavior: 'smooth' });
  }, [items]);

  // Rejoue la file d'attente au retour de la connexion (et au chargement si déjà en ligne).
  // biome-ignore lint/correctness/useExhaustiveDependencies: handler stable, exécuté au montage
  useEffect(() => {
    const surOnline = () => void reprendreFile();
    window.addEventListener('online', surOnline);
    surOnline();
    return () => window.removeEventListener('online', surOnline);
  }, []);

  useEffect(
    () => () => {
      if (fermeturePanneauRef.current !== null) {
        window.clearTimeout(fermeturePanneauRef.current);
      }
    },
    [],
  );

  const ajouter = (it: Item) => setItems((xs) => [...xs, it]);

  function ouvrirPanneauContacts(contacts: FicheContact[]): void {
    if (fermeturePanneauRef.current !== null) {
      window.clearTimeout(fermeturePanneauRef.current);
      fermeturePanneauRef.current = null;
    }
    setContactsPanneau(contacts);
    setContactPanneauId(contacts[0]?.id ?? null);
    setContactPanneauPhase('entrant');
    requestAnimationFrame(() => setContactPanneauPhase('ouvert'));
  }

  function ouvrirPanneauContact(contact: FicheContact): void {
    ouvrirPanneauContacts([contact]);
  }

  function fermerPanneauContact(): void {
    if (contactPanneauPhase === 'ferme' || contactPanneauPhase === 'sortant') return;
    setContactPanneauPhase('sortant');
    fermeturePanneauRef.current = window.setTimeout(() => {
      fermeturePanneauRef.current = null;
      setContactsPanneau([]);
      setContactPanneauId(null);
      setContactPanneauPhase('ferme');
      requestAnimationFrame(synchroniserViewportVisible);
      window.setTimeout(synchroniserViewportVisible, 80);
    }, 210);
  }

  // Mémoire COURTE : on n'envoie que les N derniers tours, pas tout l'historique.
  // Assez pour les demandes chaînées (« tague-le », « ajoute une note » -> il sait
  // de qui on parle), borné pour ne pas cramer les tokens ni atteindre le plafond
  // de 50 messages de RequeteChat (au-delà, l'API renvoie 400).
  const FENETRE_CONTEXTE = 16;
  function historique(): MessageChat[] {
    const tous = items.flatMap((it): MessageChat[] => {
      if (it.id === 'accueil') return [];
      if ('texte' in it && (it.role === 'user' || it.role === 'assistant'))
        return [{ role: it.role, contenu: it.texte }];
      // Une PROPOSITION en attente fait partie du fil : sans elle, l'agent oublie au
      // tour suivant ce qu'il vient de proposer. Quand l'utilisateur répond par un
      // message (« oui », une précision) au lieu de cliquer Confirmer, le contexte
      // de la proposition était perdu (le tool_call n'est pas persisté, et le texte
      // qui l'accompagne est souvent vide). On la reverse en message assistant.
      if (it.role === 'assistant' && 'proposition' in it)
        return [
          { role: 'assistant', contenu: `Proposition en attente : ${it.proposition.description}` },
        ];
      // Données AFFICHÉES (fiche ouverte via la recherche rapide OU par l'agent,
      // coordonnées, liste) : elles entrent dans le contexte pour que le compagnon
      // sache de QUI on parle au tour suivant (« ajoute une note dessus », « tague-le »).
      // Sans ça, une fiche ouverte par la barre de recherche restait invisible à l'agent.
      // PII (B3) : on n'injecte QUE nom + fonction + organisation, JAMAIS email ni
      // téléphone (le modèle ne voit jamais les coordonnées ; il passe par donner_coordonnees).
      if (it.role === 'assistant' && 'contact' in it) {
        const det = [it.contact.fonction, it.contact.organisation].filter(Boolean).join(', ');
        return [
          {
            role: 'assistant',
            contenu: `[Fiche affichée] ${it.contact.nom}${det ? ` (${det})` : ''}`,
          },
        ];
      }
      if (it.role === 'assistant' && 'coordonnees' in it)
        return [{ role: 'assistant', contenu: `[Coordonnées affichées] ${it.coordonnees.nom}` }];
      if (it.role === 'assistant' && 'notesRecall' in it) {
        // B3 : on ne réinjecte PAS le texte réel des notes au modèle (il ne l'a jamais vu).
        // Juste un repère + les personnes nommées, pour le suivi conversationnel.
        const qui = [
          ...new Set(it.notesRecall.map((n) => n.personne).filter((p): p is string => !!p)),
        ].join(', ');
        return [
          {
            role: 'assistant',
            contenu: `[${it.notesRecall.length} note(s) affichée(s)${qui ? ` — ${qui}` : ''}]`,
          },
        ];
      }
      if (it.role === 'assistant' && 'liste' in it) {
        // On sérialise les NOMS (numérotés, plafonnés) dans l'historique : l'agent garde
        // le contexte pour un suivi (« tague-le », « ouvre le deuxième ») même si la prose
        // visible ne recopie pas la liste (le tableau cliquable l'affiche, revue Codex).
        const noms = it.liste
          .slice(0, 15)
          .map((c, i) => {
            // Nom + fonction/organisation (comme le tableau visible) : permet de résoudre
            // un suivi par qualificatif (« celle de la mairie ») et les homonymes (revue Codex).
            const sous = [c.fonction, c.organisation].filter(Boolean).join(' · ');
            return `${i + 1}. ${c.nom}${sous ? ` (${sous})` : ''}`;
          })
          .join(', ');
        return [
          {
            role: 'assistant',
            contenu: `[Liste de ${it.liste.length} contacts affichée : ${noms}]`,
          },
        ];
      }
      if (it.role === 'assistant' && 'entreprises' in it) {
        const noms = it.entreprises
          .slice(0, 15)
          .map((e, i) => `${i + 1}. ${e.nom}`)
          .join(', ');
        return [
          {
            role: 'assistant',
            contenu: `[Liste de ${it.entreprises.length} entreprises affichée : ${noms}]`,
          },
        ];
      }
      return [];
    });
    const contactActif: MessageChat[] = contactOuvert
      ? [
          {
            role: 'assistant',
            contenu: `[Fiche affichée] ${contactOuvert.nom}${
              [contactOuvert.fonction, contactOuvert.organisation].filter(Boolean).length
                ? ` (${[contactOuvert.fonction, contactOuvert.organisation].filter(Boolean).join(', ')})`
                : ''
            }`,
          },
        ]
      : [];
    return [...tous, ...contactActif].slice(-FENETRE_CONTEXTE);
  }

  // Envoie le message au chat et affiche la réponse (texte ou proposition). Retourne
  // l'issue : `ok` (envoyé/affiché), et `reseau` (l'échec est une panne réseau, à
  // rejouer — vs une erreur serveur, qu'il est inutile de rejouer).
  async function traiterReponse(
    texteUtilisateur: string,
  ): Promise<{ ok: boolean; reseau: boolean }> {
    setEnCours(true);
    try {
      const hist = [...historique(), { role: 'user' as const, contenu: texteUtilisateur }];
      const rep = await envoyerChat(hist);
      if (rep.kind === 'message') {
        ajouter({ id: id(), role: 'assistant', texte: rep.texte });
        if (rep.avertissements?.length) {
          for (const avertissement of rep.avertissements) {
            ajouter({ id: id(), role: 'systeme', texte: avertissement });
          }
        }
        // Liste complète demandée : rendue comme vraie liste (fiable, scrollable),
        // pas dans la prose de l'agent qui tronque (cf. ReponseChat.contacts).
        if (rep.contacts?.length) {
          ajouter({ id: id(), role: 'assistant', liste: rep.contacts });
        }
        if (rep.entreprises?.length) {
          ajouter({ id: id(), role: 'assistant', entreprises: rep.entreprises });
        }
        // Coordonnées (B3) : résolues côté serveur, affichées ici (le modèle ne les
        // a jamais vues). Une carte par contact, avec boutons Appeler / SMS / Mail.
        if (rep.coordonnees?.length) {
          for (const co of rep.coordonnees) {
            ajouter({ id: id(), role: 'assistant', coordonnees: co });
          }
        }
        // Fiche(s) riche(s) : l'agent a appelé lire_fiche → on rend la MÊME carte
        // détaillée dans le panneau de fiche (tags, notes, boutons), pour que les
        // résultats ne s'empilent pas comme des messages dans le fil.
        if (rep.fiches?.length) {
          ouvrirPanneauContacts(rep.fiches);
        }
        // Notes retrouvées (recall) : texte RÉEL via canal serveur (le modèle ne voit
        // que le masqué). Carte dédiée + bouton « Rattacher » sur les notes orphelines.
        if (rep.notes?.length) {
          ajouter({ id: id(), role: 'assistant', notesRecall: rep.notes });
        }
      } else {
        if (rep.texte) ajouter({ id: id(), role: 'assistant', texte: rep.texte });
        ajouter({
          id: id(),
          role: 'assistant',
          proposition: {
            description: rep.description,
            jeton: rep.jeton,
            ...(rep.contactId ? { contactId: rep.contactId } : {}),
            ...(rep.outil ? { outil: rep.outil } : {}),
          },
        });
      }
      return { ok: true, reseau: false };
    } catch (e) {
      // Panne RÉSEAU = ErreurApi sans statut (aucune réponse du serveur). Tout le
      // reste (4xx/5xx) = le serveur a répondu : on affiche le message d'erreur RÉEL
      // (#17) et on NE rejoue PAS (rejouer une requête invalide ne change rien).
      const reseau = e instanceof ErreurApi && e.statut === undefined;
      ajouter({
        id: id(),
        role: 'systeme',
        texte: reseau
          ? '📡 Hors-ligne : message en attente, il partira au retour de la connexion.'
          : `⚠️ ${e instanceof Error ? e.message : 'Échec de l’envoi.'}`,
      });
      return { ok: false, reseau };
    } finally {
      setEnCours(false);
    }
  }

  async function envoyerTexte(texte: string) {
    ajouter({ id: id(), role: 'user', texte });
    // Carte scannée en attente + affirmation courte ("enregistre", "oui"...) -> on
    // crée le contact avec les données extraites, au lieu d'envoyer au chat qui ne les
    // voit pas (sinon il redemande nom + adresse déjà sur la carte, cf. friction Café).
    if (ficheEnAttente && estAffirmationEnregistrer(texte)) {
      const carte = ficheEnAttente;
      setFicheEnAttente(null);
      await creerContact(carte);
      return;
    }
    // Message autre : la carte n'est plus le contexte immédiat (fenêtre = le message
    // qui suit le scan), on referme la fenêtre d'enregistrement par affirmation.
    setFicheEnAttente(null);
    // On TENTE TOUJOURS l'envoi : `navigator.onLine` est un faux négatif notoire (VPN,
    // certaines configs réseau le mettent à false alors qu'on est en ligne, retour Kamil).
    // C'est l'échec RÉSEAU réel (traiterReponse -> r.reseau) qui met en file, pas un drapeau
    // peu fiable. Sur vrai hors-ligne, le fetch échoue vite -> même résultat (file + message).
    const r = await traiterReponse(texte);
    if (!r.ok && r.reseau) mettreEnFile(texte);
  }

  // Mention @ d'utilisateurs OcéRM : notifie par push les membres cités (ce n'est PAS
  // un message à l'agent). L'auteur est résolu côté serveur via le jeton.
  async function surMention(membres: Membre[], texte: string) {
    ajouter({ id: id(), role: 'user', texte });
    // On tente toujours : l'échec réseau réel est géré dans le catch (pas de pré-check
    // sur navigator.onLine, faux négatif notoire — retour Kamil).
    const noms = membres.map((m) => m.nom).join(', ');
    try {
      const { notifies } = await envoyerMention(
        membres.map((m) => m.id),
        texte,
      );
      ajouter({
        id: id(),
        role: 'systeme',
        texte:
          notifies > 0
            ? `✓ ${noms} notifié (push envoyé).`
            : `✓ Mention enregistrée pour ${noms} (visible dans la cloche). Aucun push reçu : l’appareil de la personne n’est pas abonné (à activer dans la cloche, OcéRM installé sur l’écran d’accueil).`,
      });
    } catch {
      ajouter({
        id: id(),
        role: 'systeme',
        texte: `La mention de ${noms} n’a pas pu être envoyée.`,
      });
    }
  }

  // Commande slash « / » : mentionner des CONTACTS du carnet (Nassir, Sophie...) pour
  // leur ajouter une note (ou mettre à jour un champ). Le serveur signe une proposition
  // déterministe (pas d'agent), confirmée via /action. Tente toujours (échec réseau géré
  // dans le catch ; pas de pré-check navigator.onLine, faux négatif notoire).
  async function envoyerNoteMention(contacts: ContactResume[], texte: string) {
    ajouter({ id: id(), role: 'user', texte });
    setFicheEnAttente(null);
    setEnCours(true);
    try {
      const rep = await proposerNoteMention(
        contacts.map((ct) => ct.id),
        texte,
      );
      if (rep.texte) ajouter({ id: id(), role: 'assistant', texte: rep.texte });
      ajouter({
        id: id(),
        role: 'assistant',
        proposition: { description: rep.description, jeton: rep.jeton },
      });
    } catch (e) {
      ajouter({
        id: id(),
        role: 'systeme',
        texte: `⚠️ ${e instanceof Error ? e.message : 'Échec de la note.'}`,
      });
    } finally {
      setEnCours(false);
    }
  }

  // Au retour du réseau : on rejoue les messages en attente. On ne retire un message
  // qu'APRÈS son traitement (succès OU erreur serveur définitive) : un rechargement
  // pendant le rejeu ne perd plus rien. Une panne réseau interrompt le rejeu (le reste
  // attend le prochain retour). Verrou anti-double-rejeu (online + montage).
  async function reprendreFile() {
    // Pas de gate sur navigator.onLine (faux négatif notoire qui empêcherait la file de
    // se vider alors qu'on est en ligne) : on TENTE le rejeu ; un échec RÉSEAU réel
    // (r.reseau) interrompt et laisse le reste en attente du prochain essai.
    if (rejeuEnCours.current) return;
    if (lireFile().length === 0) return;
    rejeuEnCours.current = true;
    try {
      for (const item of lireFile()) {
        ajouter({ id: id(), role: 'user', texte: item.texte, enAttente: true });
        const r = await traiterReponse(item.texte);
        if (r.ok || !r.reseau) retirerDeFile(item.id);
        else break; // réseau réellement retombé : inutile d'insister sur les suivants
      }
    } finally {
      rejeuEnCours.current = false;
    }
  }

  // Mémo vocal : envoyé, transcrit côté serveur, traité par l'agent, PUIS le son
  // est jeté (jamais conservé : le blob reste local et n'est pas persisté).
  async function envoyerVocal(blob: Blob) {
    setEnCours(true);
    let texte = '';
    try {
      texte = (await transcrire(blob)).trim();
    } catch {
      /* transcription indisponible */
    }
    setEnCours(false);
    if (!texte) {
      ajouter({ id: id(), role: 'systeme', texte: "Je n'ai rien entendu. Réessaie." });
      return;
    }
    ajouter({ id: id(), role: 'user', texte, vocal: true });
    await traiterReponse(texte);
  }

  async function surImage(fichier: File) {
    const apercu = URL.createObjectURL(fichier);
    urlsBlob.current.add(apercu);
    ajouter({ id: id(), role: 'user', imageUrl: apercu });
    setEnCours(true);
    try {
      const photo = await compresserImage(fichier);
      const res = await extraireCarte(photo);
      if (res.trouve && res.contact) {
        ajouter({ id: id(), role: 'assistant', texte: MESSAGE_CARTE_LUE });
        ajouter({ id: id(), role: 'assistant', fiche: res.contact });
        setFicheEnAttente(res.contact);
      } else {
        // Image analysée mais sans coordonnées : on dit ce que c'est, pas d'échec.
        ajouter({
          id: id(),
          role: 'assistant',
          texte: `J'ai analysé l'image : ${res.description ?? "je n'y vois pas de coordonnées de contact"}. Que veux-tu en faire ?`,
        });
      }
    } catch {
      ajouter({
        id: id(),
        role: 'systeme',
        texte:
          "Lecture de la carte impossible. Vérifie que c'est bien une photo (JPEG, PNG ou HEIC) et réessaie.",
      });
    } finally {
      setEnCours(false);
    }
  }

  // Import de photos déjà prises (pellicule / fichiers) : on traite chaque carte
  // l'une après l'autre (séquentiel) pour ne pas lancer N analyses vision en
  // parallèle (coût + ordre des fiches dans le fil).
  async function surImages(fichiers: File[]) {
    for (const fichier of fichiers) await surImage(fichier);
  }

  // Libère toutes les URLs objet d'aperçu au démontage (anti-fuite mémoire).
  useEffect(() => {
    const urls = urlsBlob.current;
    return () => {
      for (const u of urls) URL.revokeObjectURL(u);
    };
  }, []);

  // Renvoie true seulement si le serveur a confirmé la création (la carte
  // n'affiche « Envoyé » qu'en cas de succès réel, jamais en cas d'échec).
  async function creerContact(fiche: ContactExtrait, forcer = false): Promise<boolean> {
    // Toute tentative d'enregistrement (bouton OU affirmation) referme la fenêtre.
    setFicheEnAttente(null);
    setEnCours(true);
    try {
      // Étape 2a : /contacts détecte un doublon OU signe la proposition d'écriture.
      const r = await proposerFiche(fiche, forcer);
      // Doublon possible (#9) : carte sans email/tél ressemblant à des fiches
      // existantes. Rien n'est créé : on remonte les candidats et l'utilisateur
      // tranche (« créer quand même » ou ouvrir une fiche). Pas d'auto-fusion.
      if ('doublonPossible' in r) {
        ajouter({
          id: id(),
          role: 'assistant',
          doublon: { contact: fiche, candidats: r.candidats },
        });
        return false;
      }
      // Étape 2b : le clic « Créer la fiche » EST la confirmation humaine. On
      // exécute la proposition signée via /action (un seul chemin d'écriture, W4) ;
      // pas de 2e clic. La fiche créée/fusionnée revient dans `resultat`.
      const { resultat } = await executerAction(r.jeton);
      const f = resultat as Fiche;
      // La carte existait déjà (rescan) : aucun doublon créé. On dit ce que la fusion
      // a RÉELLEMENT complété (jamais un faux « à jour » si on a comblé des champs),
      // ou qu'il n'y avait rien à compléter. Sinon, création neuve confirmée.
      let base: string;
      if (f.deja) {
        const completes = f.complete ?? [];
        base = completes.length
          ? `Ce contact était déjà dans ton carnet : **${f.nom}**. J'ai complété : ${completes.join(', ')}.`
          : `Ce contact est déjà dans ton carnet : **${f.nom}** (déjà à jour, rien à compléter).`;
      } else {
        base = `✓ Fiche créée : **${f.nom}**`;
      }
      // Champs du scan qui diffèrent d'une valeur déjà saisie : non écrasés, signalés.
      const conflits = f.conflits ?? [];
      if (conflits.length) {
        base += `\n\n⚠️ Différent du scan (gardé tel quel, à vérifier) : ${conflits.join(', ')}.`;
      }
      // L'adresse n'a pas pu être attachée : on le signale, l'utilisateur n'est pas
      // laissé à croire qu'elle est enregistrée.
      let texte = base;
      if (f.adresseEnregistree === false) {
        texte += "\n\n⚠️ L'adresse n'a pas pu être enregistrée.";
      }
      if (f.notesImportEnregistrees === false) {
        texte += "\n\n⚠️ La note, l'URL ou l'anniversaire importé n'a pas pu être enregistré.";
      }
      // Numéro illisible non stocké (#11) : on le dit, plutôt que de laisser croire
      // qu'il est enregistré. Par champ (fixe / mobile).
      const numKo = [
        f.mobileEnregistre === false ? 'le mobile' : null,
        f.telephoneEnregistre === false ? 'le téléphone fixe' : null,
      ].filter(Boolean);
      if (numKo.length) {
        texte += `\n\n⚠️ Numéro non enregistré (${numKo.join(' et ')}) : illisible, corrige-le et réessaie.`;
      }
      ajouter({ id: id(), role: 'assistant', texte });
      return true;
    } catch {
      ajouter({ id: id(), role: 'systeme', texte: 'Création impossible. Réessaie.' });
      return false;
    } finally {
      setEnCours(false);
    }
  }

  async function confirmer(
    p: Proposition,
    options: { noteComplementaire?: string } = {},
  ): Promise<boolean> {
    setEnCours(true);
    try {
      const r = await executerAction(p.jeton, options);
      ajouter({ id: id(), role: 'assistant', texte: `✓ Fait : ${p.description}` });
      // Action sur un contact (création / maj / note / tag) -> on AFFICHE sa fiche à jour
      // pour la voir immédiatement (best-effort : un échec de relecture ne casse rien).
      if (r.contactId) {
        try {
          const contact = await lireContact(r.contactId);
          ouvrirPanneauContact(contact);
        } catch {
          /* fiche indisponible : l'action a réussi, l'affichage se fera au prochain accès */
        }
      }
      return true;
    } catch (e) {
      ajouter({
        id: id(),
        role: 'systeme',
        texte: e instanceof ErreurApi ? e.message : 'Action impossible. Réessaie.',
      });
      return false;
    } finally {
      setEnCours(false);
    }
  }

  // Sélection d'un contact dans la recherche : on affiche la fiche complète
  // (données + notes + actions appeler / SMS / mail), pas un simple résumé.
  async function choisirContact(c: ContactResume) {
    setEnCours(true);
    try {
      const contact = await lireContact(c.id);
      ouvrirPanneauContact(contact);
    } catch {
      ajouter({ id: id(), role: 'systeme', texte: 'Fiche indisponible. Réessaie.' });
    } finally {
      setEnCours(false);
    }
  }

  // Ouvre la fiche d'un contact depuis une notification (clic sur une notif liée).
  async function ouvrirContactDepuisNotification(contactId: string) {
    setEnCours(true);
    try {
      const contact = await lireContact(contactId);
      ouvrirPanneauContact(contact);
    } catch {
      ajouter({ id: id(), role: 'systeme', texte: 'Fiche indisponible. Réessaie.' });
    } finally {
      setEnCours(false);
    }
  }

  // Remplace une fiche affichée par sa version fraîche (après une édition de champ).
  function remplacerContact(contact: FicheContact): void {
    setContactsPanneau((actuels) =>
      actuels.map((fiche) => (fiche.id === contact.id ? contact : fiche)),
    );
    setItems((actuels) =>
      actuels.map((item) =>
        'contact' in item && item.contact.id === contact.id ? { ...item, contact } : item,
      ),
    );
  }

  function retirerContactAffiche(contactId: string): void {
    setItems((actuels) =>
      actuels.flatMap((item): Item[] => {
        if ('contact' in item && item.contact.id === contactId) return [];
        if ('liste' in item) {
          const liste = item.liste.filter((contact) => contact.id !== contactId);
          if (liste.length === 0) return [];
          const itemListe: Item = { id: item.id, role: 'assistant', liste };
          return [itemListe];
        }
        return [item];
      }),
    );
  }

  function retirerContactDuPanneau(contactId: string): void {
    const index = contactsPanneau.findIndex((fiche) => fiche.id === contactId);
    const restants = contactsPanneau.filter((fiche) => fiche.id !== contactId);
    const prochainId = restants[Math.max(0, Math.min(index, restants.length - 1))]?.id ?? null;
    setContactsPanneau(restants);
    if (prochainId) {
      setContactPanneauId(prochainId);
      return;
    }
    if (fermeturePanneauRef.current !== null) {
      window.clearTimeout(fermeturePanneauRef.current);
      fermeturePanneauRef.current = null;
    }
    setContactPanneauId(null);
    setContactPanneauPhase('ferme');
    requestAnimationFrame(synchroniserViewportVisible);
  }

  // Suppression de fiche : on prépare d'abord la proposition signée côté serveur,
  // puis la modale affiche CE libellé exact avant /action. Après succès, on retire
  // localement la fiche sans tenter de la relire (elle n'existe plus).
  async function preparerSuppressionContactDepuisFiche(
    contact: FicheContact,
  ): Promise<Proposition | null> {
    setEnCours(true);
    try {
      return await proposerSuppressionContact(contact.id);
    } catch (e) {
      ajouter({
        id: id(),
        role: 'systeme',
        texte: e instanceof ErreurApi ? e.message : 'Suppression impossible. Réessaie.',
      });
      return null;
    } finally {
      setEnCours(false);
    }
  }

  async function supprimerContactDepuisFiche(
    contact: FicheContact,
    proposition: Proposition,
  ): Promise<boolean> {
    setEnCours(true);
    try {
      await executerAction(proposition.jeton);
      retirerContactAffiche(contact.id);
      retirerContactDuPanneau(contact.id);
      ajouter({ id: id(), role: 'systeme', texte: `✓ ${proposition.description}` });
      return true;
    } catch (e) {
      ajouter({
        id: id(),
        role: 'systeme',
        texte: e instanceof ErreurApi ? e.message : 'Suppression impossible. Réessaie.',
      });
      return false;
    } finally {
      setEnCours(false);
    }
  }

  // Note de fiche (ajout / édition / suppression) : `faire` renvoie une proposition
  // signée -> on confirme via /action -> on relit la fiche (l'appelant connaît le
  // contactId). Aucune écriture sans cette confirmation (règle 3).
  async function executerNote(
    contactId: string,
    faire: () => Promise<{ jeton: string }>,
  ): Promise<boolean> {
    setEnCours(true);
    try {
      const { jeton } = await faire();
      await executerAction(jeton);
      try {
        remplacerContact(await lireContact(contactId));
      } catch {
        /* relecture best-effort : la note est écrite, l'affichage se resync au prochain accès */
      }
      return true;
    } catch (e) {
      ajouter({
        id: id(),
        role: 'systeme',
        texte: e instanceof ErreurApi ? e.message : 'Action impossible. Réessaie.',
      });
      return false;
    } finally {
      setEnCours(false);
    }
  }

  // Édition inline d'un champ de fiche : propose (signé) -> confirme via /action ->
  // relit la fiche pour refléter la valeur écrite. Aucune écriture sans cette confirmation
  // serveur (le jeton signé est le seul chemin), conforme à la règle 3.
  async function modifierChampContact(
    contactId: string,
    champ: ChampFicheContactModifiable,
    valeur: string,
  ): Promise<boolean> {
    setEnCours(true);
    try {
      const proposition = await proposerMajChampContact(contactId, champ, valeur);
      await executerAction(proposition.jeton);
      // Écriture CONFIRMÉE. La relecture est best-effort : un échec de GET (réseau /
      // lecture Twenty) ne doit PAS faire croire que l'enregistrement a échoué et
      // pousser l'utilisateur à réécrire ce qui est déjà écrit (revue Codex).
      try {
        remplacerContact(await lireContact(contactId));
      } catch {
        /* valeur écrite ; l'affichage se resynchronisera au prochain accès à la fiche */
      }
      return true;
    } catch (e) {
      ajouter({
        id: id(),
        role: 'systeme',
        texte: e instanceof ErreurApi ? e.message : 'Mise à jour impossible. Réessaie.',
      });
      return false;
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="appli">
      <MajPwa />
      <Entete
        connecte={connecte}
        prenom={prenom}
        onOuvrirContact={ouvrirContactDepuisNotification}
        onDeconnecter={() => {
          deconnecter();
          setConnecte(false);
        }}
      />
      <main className="fil">
        {items.length <= 1 ? (
          <div className="accueil-hero">
            <img className="accueil-logo" src="/oceinde-logo.png" alt="Groupe Océinde" />
            <h1 className="accueil-titre">Bonjour</h1>
            <p className="accueil-sous">
              Ton assistant relationnel. Demande-moi de <strong>chercher</strong>,{' '}
              <strong>ajouter</strong> ou <strong>résumer</strong> un contact — à l'écrit, en
              photographiant une carte, ou à la voix.
            </p>
          </div>
        ) : (
          items
            .filter((it) => it.id !== 'accueil')
            .map((it) => (
              <Bulle
                key={it.id}
                item={it}
                onCreer={creerContact}
                onConfirmer={confirmer}
                onChoisir={choisirContact}
                onOuvrirContact={ouvrirContactDepuisNotification}
                onModifierContact={modifierChampContact}
                onActionNote={executerNote}
                onPreparerSuppressionContact={preparerSuppressionContactDepuisFiche}
                onSupprimerContact={supprimerContactDepuisFiche}
                desactive={enCours}
                connecte={connecte}
              />
            ))
        )}
        {enCours && (
          <div className="bulle gauche bulle-typing" role="status" aria-live="polite">
            <span className="typing">
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </span>
            <span className="typing-label">OcéRM réfléchit…</span>
            <span className="sr-only">L'assistant traite ton message</span>
          </div>
        )}
        <div ref={finRef} />
      </main>
      <Saisie
        onTexte={envoyerTexte}
        onNoteMention={envoyerNoteMention}
        onMention={surMention}
        onImage={surImage}
        onImages={surImages}
        onVocal={envoyerVocal}
        onChoisir={choisirContact}
        desactive={enCours}
        connecte={connecte}
      />
      {contactOuvert && contactPanneauPhase !== 'ferme' && (
        <PanneauContact
          contact={contactOuvert}
          contacts={contactsPanneau}
          phase={contactPanneauPhase}
          onChoisir={setContactPanneauId}
          onFermer={fermerPanneauContact}
          onModifierContact={modifierChampContact}
          onActionNote={executerNote}
          onPreparerSuppressionContact={preparerSuppressionContactDepuisFiche}
          onSupprimerContact={supprimerContactDepuisFiche}
          desactive={enCours}
          connecte={connecte}
        />
      )}
    </div>
  );
}

function Entete({
  connecte,
  prenom,
  onOuvrirContact,
  onDeconnecter,
}: {
  connecte: boolean;
  prenom: string | null;
  onOuvrirContact: (contactId: string) => Promise<void>;
  onDeconnecter: () => void;
}) {
  // La recherche de contacts a fusionné dans le composer (omnibox, cf. Saisie) :
  // un seul champ pour chercher une fiche ET parler à l'agent. L'en-tête ne porte
  // plus que le logo et les actions.
  return (
    <header className="entete">
      <div className="entete-haut">
        <span className="logo-bloc">
          <span className="logo">
            <span className="oce">Océ</span>
            <span className="rm">RM</span>
          </span>
          <span className="version" title={`Build ${__BUILD_ID__} · ${__BUILD_DATE__}`}>
            v{__APP_VERSION__} · {__BUILD_ID__}
          </span>
        </span>
        <div className="entete-actions">
          <BoutonInstaller />
          {oauthConfigure() &&
            (connecte ? (
              <button
                type="button"
                className={prenom ? 'connecte connecte-nom' : 'connecte'}
                title={`Connecté${prenom ? ` (${prenom})` : ''} : cliquer pour se déconnecter`}
                onClick={onDeconnecter}
              >
                {prenom ? prenom : <IconeCompte size={20} />}
              </button>
            ) : (
              <button type="button" className="btn-connexion" onClick={() => void seConnecter()}>
                Se connecter
              </button>
            ))}
          <BoutonImport />
          <BoutonNotifications connecte={connecte} onOuvrirContact={onOuvrirContact} />
        </div>
      </div>
    </header>
  );
}

// Aperçu d'une photo envoyée. Si le navigateur ne sait pas la rendre (ex. HEIC
// iPhone sur Chrome), on affiche un repli neutre au lieu d'une image cassée
// (le scan, lui, fonctionne via la conversion HEIC côté compression). On couvre
// aussi le cas « chargée mais vide » (naturalWidth 0), pas seulement onError.
function ApercuImage({ url }: { url: string }) {
  const [casse, setCasse] = useState(false);
  if (casse) return <span className="apercu-repli">📷 Photo envoyée</span>;
  return (
    <img
      className="bulle-image"
      src={url}
      alt="Carte envoyée"
      onError={() => setCasse(true)}
      onLoad={(e) => {
        if (e.currentTarget.naturalWidth === 0) setCasse(true);
      }}
    />
  );
}

function Bulle({
  item,
  onCreer,
  onConfirmer,
  onChoisir,
  onOuvrirContact,
  onModifierContact,
  onActionNote,
  onPreparerSuppressionContact,
  onSupprimerContact,
  desactive,
  connecte,
}: {
  item: Item;
  onCreer: (f: ContactExtrait, forcer?: boolean) => Promise<boolean>;
  onConfirmer: (p: Proposition) => Promise<boolean>;
  onChoisir: (c: ContactResume) => void;
  onOuvrirContact: (contactId: string) => Promise<void>;
  onModifierContact: (
    contactId: string,
    champ: ChampFicheContactModifiable,
    valeur: string,
  ) => Promise<boolean>;
  onActionNote: (contactId: string, faire: () => Promise<{ jeton: string }>) => Promise<boolean>;
  onPreparerSuppressionContact: (contact: FicheContact) => Promise<Proposition | null>;
  onSupprimerContact: (contact: FicheContact, proposition: Proposition) => Promise<boolean>;
  desactive: boolean;
  connecte: boolean;
}) {
  if (item.role === 'systeme') return <p className="statut-erreur">{item.texte}</p>;

  const cote = item.role === 'user' ? 'droite' : 'gauche';
  return (
    <div className={`bulle ${cote}`}>
      {'imageUrl' in item && <ApercuImage url={item.imageUrl} />}
      {'vocal' in item && item.vocal && <span className="bulle-vocal">🎙️ Mémo vocal</span>}
      {'enAttente' in item && item.enAttente && <span className="bulle-attente">🕓 envoyé</span>}
      {'texte' in item && <Markdown texte={item.texte} />}
      {'fiche' in item && <CarteFiche fiche={item.fiche} onCreer={onCreer} desactive={desactive} />}
      {'doublon' in item && (
        <CarteDoublon
          contact={item.doublon.contact}
          candidats={item.doublon.candidats}
          onCreer={onCreer}
          onChoisir={onChoisir}
          desactive={desactive}
        />
      )}
      {'contact' in item && (
        <CarteContact
          c={item.contact}
          onModifier={onModifierContact}
          onActionNote={onActionNote}
          onPreparerSuppression={onPreparerSuppressionContact}
          onSupprimer={onSupprimerContact}
          desactive={desactive}
          connecte={connecte}
        />
      )}
      {'coordonnees' in item && <CarteCoordonnees co={item.coordonnees} />}
      {'notesRecall' in item && (
        <CarteNotesRecall
          notes={item.notesRecall}
          onActionNote={onActionNote}
          desactive={desactive}
          connecte={connecte}
        />
      )}
      {'liste' in item && <CarteListe contacts={item.liste} onChoisir={onChoisir} />}
      {'entreprises' in item && <CarteEntreprises entreprises={item.entreprises} />}
      {'proposition' in item && (
        <CarteProposition
          p={item.proposition}
          onConfirmer={onConfirmer}
          onOuvrirContact={onOuvrirContact}
          desactive={desactive}
        />
      )}
    </div>
  );
}

function PanneauContact({
  contact,
  contacts,
  phase,
  onChoisir,
  onFermer,
  onModifierContact,
  onActionNote,
  onPreparerSuppressionContact,
  onSupprimerContact,
  desactive,
  connecte,
}: {
  contact: FicheContact;
  contacts: FicheContact[];
  phase: PhasePanneauContact;
  onChoisir: (contactId: string) => void;
  onFermer: () => void;
  onModifierContact: (
    contactId: string,
    champ: ChampFicheContactModifiable,
    valeur: string,
  ) => Promise<boolean>;
  onActionNote: (contactId: string, faire: () => Promise<{ jeton: string }>) => Promise<boolean>;
  onPreparerSuppressionContact: (contact: FicheContact) => Promise<Proposition | null>;
  onSupprimerContact: (contact: FicheContact, proposition: Proposition) => Promise<boolean>;
  desactive: boolean;
  connecte: boolean;
}) {
  useEffect(() => {
    const surEchap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFermer();
    };
    window.addEventListener('keydown', surEchap);
    return () => window.removeEventListener('keydown', surEchap);
  }, [onFermer]);

  return (
    <div className="contact-panneau-overlay" data-state={phase} role="presentation">
      <aside
        className="contact-panneau"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-panneau-titre"
      >
        <div className="contact-panneau-entete">
          <div className="contact-panneau-titres">
            <span className="contact-panneau-label" id="contact-panneau-titre">
              Fiche contact
            </span>
            {contacts.length > 1 && (
              <span className="contact-panneau-compteur">
                {contacts.findIndex((c) => c.id === contact.id) + 1}/{contacts.length}
              </span>
            )}
          </div>
          <button
            type="button"
            className="contact-panneau-fermer"
            onClick={onFermer}
            aria-label="Fermer la fiche"
          >
            ×
          </button>
        </div>
        {contacts.length > 1 && (
          <div className="contact-panneau-tabs" role="tablist" aria-label="Fiches ouvertes">
            {contacts.map((fiche) => (
              <button
                key={fiche.id}
                type="button"
                role="tab"
                aria-selected={fiche.id === contact.id}
                className={fiche.id === contact.id ? 'actif' : undefined}
                onClick={() => onChoisir(fiche.id)}
              >
                {fiche.nom}
              </button>
            ))}
          </div>
        )}
        <div className="contact-panneau-contenu">
          <CarteContact
            c={contact}
            onModifier={onModifierContact}
            onActionNote={onActionNote}
            onPreparerSuppression={onPreparerSuppressionContact}
            onSupprimer={onSupprimerContact}
            desactive={desactive}
            connecte={connecte}
          />
        </div>
      </aside>
    </div>
  );
}

// Les champs tableau (`agences`, `emailsSecondaires`, `telephonesSupplementaires`, `urls`)
// ne sont pas rendus comme
// inputs texte dans la carte de validation du scan.
const CHAMPS_FICHE = [
  'prenom',
  'nom',
  'fonction',
  'organisation',
  'email',
  'mobile',
  'telephone',
  'ville',
  'adresse',
] as const;
const LIBELLES_CHAMP: Record<(typeof CHAMPS_FICHE)[number], string> = {
  prenom: 'Prénom',
  nom: 'Nom',
  fonction: 'Fonction',
  organisation: 'Organisation',
  email: 'E-mail',
  mobile: 'Mobile',
  telephone: 'Téléphone fixe',
  ville: 'Ville',
  adresse: 'Adresse',
};

function CarteFiche({
  fiche,
  onCreer,
  desactive,
}: {
  fiche: ContactExtrait;
  onCreer: (f: ContactExtrait, forcer?: boolean) => Promise<boolean>;
  desactive: boolean;
}) {
  const [c, setC] = useState(fiche);
  const [etat, setEtat] = useState<'edition' | 'envoi' | 'fait'>('edition');
  const maj = (k: keyof ContactExtrait) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setC({ ...c, [k]: e.target.value || undefined });
  const infosImportees = [
    ...(c.emailsSecondaires ?? []).map((v) => `E-mail secondaire : ${v}`),
    ...(c.emailsInvalides ?? []).map((v) => `E-mail à corriger : ${v}`),
    ...(c.telephonesSupplementaires ?? []).map((v) => `Téléphone supplémentaire : ${v}`),
    ...(c.telephonesSpeciaux ?? []).map((v) => `Téléphone libellé fax/pager : ${v}`),
    ...(c.urls ?? []).map((v) => `URL : ${v}`),
    ...(c.urlsNonNormalisees ?? []).map((v) => `URL à vérifier : ${v}`),
    ...(c.adressesSecondaires ?? []).map((v) => `Adresse secondaire : ${v}`),
    ...(c.messageries ?? []).map((v) => `Messagerie : ${v}`),
    ...(c.relations ?? []).map((v) => `Relation : ${v}`),
    ...(c.metadonneesVCard ?? []).map((v) => `Métadonnée vCard : ${v}`),
    ...(c.anniversaire ? [`Anniversaire : ${c.anniversaire}`] : []),
  ];

  return (
    <div className="carte">
      {CHAMPS_FICHE.map((k) => (
        <label className="carte-champ" key={k}>
          <span>{LIBELLES_CHAMP[k]}</span>
          <input value={c[k] ?? ''} onChange={maj(k)} disabled={etat !== 'edition'} />
        </label>
      ))}
      {c.agences && c.agences.length > 0 && (
        <div className="carte-agences">
          <span className="carte-agences-titre">
            {c.agences.length} agence{c.agences.length > 1 ? 's' : ''} de l'entreprise (enregistrée
            {c.agences.length > 1 ? 's' : ''} sur la fiche entreprise)
          </span>
          <ul>
            {c.agences.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}
      {infosImportees.length > 0 && (
        <div className="carte-agences">
          <span className="carte-agences-titre">Informations importées</span>
          <ul>
            {infosImportees.map((info) => (
              <li key={info}>{info}</li>
            ))}
          </ul>
        </div>
      )}
      {etat !== 'fait' ? (
        <button
          type="button"
          className="btn-principal"
          disabled={desactive || etat === 'envoi'}
          onClick={async () => {
            setEtat('envoi');
            // « Envoyé » ne s'affiche QUE si le serveur a confirmé la création.
            setEtat((await onCreer(c)) ? 'fait' : 'edition');
          }}
        >
          {etat === 'envoi' ? 'Création…' : 'Créer la fiche'}
        </button>
      ) : (
        <p className="carte-ok">✓ Envoyé</p>
      )}
    </div>
  );
}

/** Alerte « doublon possible » (#9) : une carte sans email/tél ressemble à des fiches
 *  existantes. On NE crée rien d'office : on liste les candidats (cliquables pour
 *  ouvrir la fiche) et on propose « créer quand même » (décision focalisée, pas de
 *  CTA concurrents). Une seule action de création possible à la fois. */
function CarteDoublon({
  contact,
  candidats,
  onCreer,
  onChoisir,
  desactive,
}: {
  contact: ContactExtrait;
  candidats: CandidatDoublon[];
  onCreer: (f: ContactExtrait, forcer?: boolean) => Promise<boolean>;
  onChoisir: (c: ContactResume) => void;
  desactive: boolean;
}) {
  const [etat, setEtat] = useState<'choix' | 'envoi' | 'fait'>('choix');
  return (
    <div className="carte carte-doublon">
      <p className="doublon-titre">
        ⚠️ <strong>{[contact.prenom, contact.nom].filter(Boolean).join(' ')}</strong> ressemble à{' '}
        {candidats.length === 1 ? 'une fiche' : 'des fiches'} déjà dans ton carnet :
      </p>
      <div className="doublon-liste">
        {candidats.map((cand) => {
          const sous = [cand.fonction, cand.organisation, cand.ville].filter(Boolean).join(' · ');
          return (
            <button
              type="button"
              key={cand.id}
              className="doublon-candidat"
              onClick={() =>
                onChoisir({
                  id: cand.id,
                  nom: cand.nom,
                  fonction: cand.fonction,
                  organisation: cand.organisation,
                })
              }
            >
              <span className="doublon-nom">{cand.nom}</span>
              {sous && <span className="doublon-sous">{sous}</span>}
            </button>
          );
        })}
      </div>
      {etat !== 'fait' ? (
        <button
          type="button"
          className="btn-secondaire"
          disabled={desactive || etat === 'envoi'}
          onClick={async () => {
            setEtat('envoi');
            // « Créer quand même » : on passe outre la détection (forcer=true).
            setEtat((await onCreer(contact, true)) ? 'fait' : 'choix');
          }}
        >
          {etat === 'envoi' ? 'Création…' : 'Créer quand même une nouvelle fiche'}
        </button>
      ) : (
        <p className="carte-ok">✓ Nouvelle fiche créée</p>
      )}
    </div>
  );
}

function LigneInfo({ libelle, valeur }: { libelle: string; valeur: string | null }) {
  return (
    <div className="fiche-ligne">
      <span className="fiche-ligne-libelle">{libelle}</span>
      <span className={valeur ? 'fiche-ligne-valeur' : 'fiche-ligne-vide'}>
        {valeur || 'non renseigné'}
      </span>
    </div>
  );
}

type EtatLigneEditable = 'lecture' | 'edition' | 'enregistrement' | 'ok' | 'erreur';

/** Ligne de fiche éditable EN PLACE : clic -> champ pré-rempli + Enregistrer/Annuler.
 *  L'enregistrement passe par onModifier (proposition signée -> /action), jamais
 *  d'écriture directe (règle 3). */
function LigneInfoEditable({
  contactId,
  champ,
  libelle,
  valeur,
  onModifier,
  desactive,
}: {
  contactId: string;
  champ: ChampFicheContactModifiable;
  libelle: string;
  valeur: string | null;
  onModifier: (
    contactId: string,
    champ: ChampFicheContactModifiable,
    valeur: string,
  ) => Promise<boolean>;
  desactive: boolean;
}) {
  const [etat, setEtat] = useState<EtatLigneEditable>('lecture');
  const [saisie, setSaisie] = useState(valeur ?? '');
  const valeurCourante = valeur ?? '';
  const idChamp = `fiche-${contactId}-${champ}`;
  const estTelephone = champ === 'mobile' || champ === 'telephone';
  const typeInput = champ === 'email' ? 'email' : estTelephone ? 'tel' : 'text';
  const valeurTrimmee = saisie.trim();
  const enregistrable =
    valeurTrimmee.length > 0 &&
    valeurTrimmee !== valeurCourante.trim() &&
    etat !== 'enregistrement';

  useEffect(() => {
    if (etat !== 'edition' && etat !== 'enregistrement') setSaisie(valeurCourante);
  }, [etat, valeurCourante]);

  const ouvrir = () => {
    if (desactive) return;
    setSaisie(valeurCourante);
    setEtat('edition');
  };
  const annuler = () => {
    setSaisie(valeurCourante);
    setEtat('lecture');
  };
  const enregistrer = async () => {
    if (!enregistrable || desactive) return;
    setEtat('enregistrement');
    const ok = await onModifier(contactId, champ, valeurTrimmee);
    setEtat(ok ? 'ok' : 'erreur');
  };

  if (etat === 'edition' || etat === 'enregistrement') {
    return (
      <form
        className="fiche-ligne fiche-ligne-edition"
        onSubmit={(e) => {
          e.preventDefault();
          void enregistrer();
        }}
      >
        <label className="fiche-ligne-libelle" htmlFor={idChamp}>
          {libelle}
        </label>
        {champ === 'adresse' ? (
          <textarea
            id={idChamp}
            className="fiche-edition-champ"
            value={saisie}
            onChange={(e) => setSaisie(e.currentTarget.value)}
            disabled={desactive || etat === 'enregistrement'}
            rows={2}
          />
        ) : (
          <input
            id={idChamp}
            className="fiche-edition-champ"
            value={saisie}
            onChange={(e) => setSaisie(e.currentTarget.value)}
            disabled={desactive || etat === 'enregistrement'}
            type={typeInput}
            inputMode={typeInput}
          />
        )}
        <div className="fiche-edition-actions">
          <button className="btn-principal" type="submit" disabled={!enregistrable || desactive}>
            {etat === 'enregistrement' ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button className="btn-secondaire" type="button" onClick={annuler} disabled={desactive}>
            Annuler
          </button>
        </div>
      </form>
    );
  }

  return (
    <button
      type="button"
      className="fiche-ligne fiche-ligne-editable"
      onClick={ouvrir}
      disabled={desactive}
      aria-label={`Modifier ${libelle}`}
    >
      <span className="fiche-ligne-libelle">{libelle}</span>
      <span className={valeur ? 'fiche-ligne-valeur' : 'fiche-ligne-vide'}>
        {valeur || 'non renseigné'}
      </span>
      {etat !== 'ok' && etat !== 'erreur' && <span className="fiche-ligne-indice">Modifier</span>}
      {etat === 'ok' && <span className="fiche-ligne-statut">Enregistré</span>}
      {etat === 'erreur' && <span className="fiche-ligne-statut erreur">Non enregistré</span>}
    </button>
  );
}

// Liste complète de contacts rendue de façon fiable (l'agent ne réécrit pas les
// noms, ils viennent de ReponseChat.contacts). Chaque ligne ouvre la fiche.
// Au-delà d'un seuil, la liste devient scrollable (UX grande liste).
function CarteListe({
  contacts,
  onChoisir,
}: {
  contacts: ContactResume[];
  onChoisir: (c: ContactResume) => void;
}) {
  return (
    <div className="carte carte-liste">
      <div className="carte-liste-tete">
        {contacts.length} contact{contacts.length > 1 ? 's' : ''}
      </div>
      <ul className="liste-contacts">
        {contacts.map((c) => {
          const sousTitre = [c.fonction, c.organisation].filter(Boolean).join(' · ');
          const url = c.entreprise ? urlEntreprise(c.entreprise) : null;
          return (
            <li key={c.id} className="liste-contact-ligne">
              <span className="liste-contact-info">
                <strong>{c.nom}</strong>
                {sousTitre && <span>{sousTitre}</span>}
              </span>
              <span className="liste-contact-actions">
                <button
                  type="button"
                  className="liste-contact-voir"
                  onClick={() => onChoisir(c)}
                  aria-label={`Voir la fiche de ${c.nom}`}
                >
                  Voir
                </button>
                {url && (
                  <a
                    className="liste-contact-voir liste-contact-ouvrir"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Ouvrir l’entreprise ${c.entreprise?.nom ?? c.organisation ?? ''}`}
                  >
                    Entreprise
                  </a>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function urlEntreprise(e: EntrepriseResume): string | null {
  if (e.urlTwenty) return e.urlTwenty;
  return TWENTY_URL ? `${TWENTY_URL}/object/company/${encodeURIComponent(e.id)}` : null;
}

function CarteEntreprises({ entreprises }: { entreprises: EntrepriseResume[] }) {
  return (
    <div className="carte carte-liste">
      <div className="carte-liste-tete">
        {entreprises.length} entreprise{entreprises.length > 1 ? 's' : ''}
      </div>
      <ul className="liste-contacts">
        {entreprises.map((e) => (
          <li key={e.id} className="liste-contact-ligne">
            <span className="liste-contact-info">
              <strong>{e.nom}</strong>
            </span>
            <span className="liste-contact-actions">
              {urlEntreprise(e) && (
                <a
                  className="liste-contact-voir liste-contact-ouvrir"
                  href={urlEntreprise(e) ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                >
                  Ouvrir
                </a>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Palette des couleurs d'option Twenty -> teinte de pastille (chips de tags).
const COULEURS_TAG: Record<string, string> = {
  green: '#3fae6b',
  turquoise: '#1aa9a0',
  sky: '#3aa6e0',
  blue: '#3b6fe0',
  purple: '#8b5cf6',
  pink: '#e0529c',
  red: '#e05252',
  orange: '#e08c3a',
  yellow: '#d4a72c',
  gray: '#8a93a3',
};
function couleurTag(nom: string): string {
  return COULEURS_TAG[nom] ?? '#8a93a3';
}

/**
 * Tags d'une fiche : chips avec retrait (×) + picker pour AJOUTER un tag de la liste
 * de référence. Toute écriture passe par onActionNote (proposition signée -> /action ->
 * relecture, règle 3).
 */
function TagsFiche({
  contactId,
  tags,
  tagsErreur,
  onActionNote,
  desactive,
}: {
  contactId: string;
  tags: FicheContact['tags'];
  tagsErreur?: boolean | undefined;
  onActionNote: (contactId: string, faire: () => Promise<{ jeton: string }>) => Promise<boolean>;
  desactive: boolean;
}) {
  const [picker, setPicker] = useState(false);
  const [dispos, setDispos] = useState<TagDisponible[]>([]);
  const [disposCharges, setDisposCharges] = useState(false);
  const [chargementDispos, setChargementDispos] = useState(false);
  const [erreurDispos, setErreurDispos] = useState(false);
  const [busy, setBusy] = useState(false);
  const presents = new Set(tags.map((t) => t.label.toLowerCase()));
  const aProposer = dispos.filter((d) => !presents.has(d.tag.toLowerCase()));

  const ouvrirPicker = async () => {
    const prochain = !picker;
    setPicker(prochain);
    if (!prochain || disposCharges || chargementDispos) return;
    setChargementDispos(true);
    setErreurDispos(false);
    try {
      setDispos(await listerTagsDispo());
      setDisposCharges(true);
    } catch {
      setErreurDispos(true);
    } finally {
      setChargementDispos(false);
    }
  };
  const retirer = async (label: string) => {
    if (busy || desactive) return;
    setBusy(true);
    await onActionNote(contactId, () => proposerRetraitTag(contactId, label));
    setBusy(false);
  };
  const ajouter = async (label: string) => {
    if (busy || desactive) return;
    setBusy(true);
    const ok = await onActionNote(contactId, () => proposerAjoutTag(contactId, label));
    setBusy(false);
    if (ok) setPicker(false);
  };

  return (
    <div className="fiche-tags-bloc">
      <div className="fiche-tags">
        {tags.map((t) => (
          <span
            key={t.value}
            className="tag-chip"
            style={{ backgroundColor: couleurTag(t.couleur) }}
          >
            {t.label}
            <button
              type="button"
              className="tag-chip-x"
              onClick={() => void retirer(t.label)}
              disabled={busy || desactive}
              aria-label={`Retirer le tag ${t.label}`}
            >
              ×
            </button>
          </span>
        ))}
        <button
          type="button"
          className="tag-ajout-btn"
          onClick={() => void ouvrirPicker()}
          disabled={busy || desactive}
        >
          + Tag
        </button>
      </div>
      {picker && (
        <div className="tag-picker">
          {chargementDispos ? (
            <span className="fiche-ligne-vide">Chargement des tags…</span>
          ) : erreurDispos ? (
            <span className="fiche-ligne-vide">Tags indisponibles pour le moment.</span>
          ) : aProposer.length > 0 ? (
            aProposer.map((d) => (
              <button
                key={d.tag}
                type="button"
                className="tag-chip tag-picker-item"
                style={{ backgroundColor: couleurTag(d.couleur) }}
                onClick={() => void ajouter(d.tag)}
                disabled={busy}
              >
                {d.tag}
              </button>
            ))
          ) : disposCharges ? (
            <span className="fiche-ligne-vide">Aucun autre tag dans la liste.</span>
          ) : (
            <span className="fiche-ligne-vide">Chargement des tags…</span>
          )}
        </div>
      )}
      {tagsErreur && <p className="fiche-ligne-vide">Tags indisponibles pour le moment.</p>}
    </div>
  );
}

/** Fragment de mention en cours de frappe, détecté UNIQUEMENT sur le texte AVANT le
 *  curseur (sinon l'autocomplete se déclenche à tort quand on édite au milieu d'une
 *  note). null si on n'est pas en train d'écrire une mention @. */
function requeteMention(avantCurseur: string): string | null {
  const m = /(^|\s)@([^\s@]*)$/.exec(avantCurseur);
  return m ? (m[2] ?? '') : null;
}

/** Textarea contrôlé avec autocomplétion @ des MEMBRES OcéRM (mention dans une note).
 *  Le backend détecte « @<membre> » dans le contenu et envoie une notif note_mention ;
 *  ce champ aide juste à insérer le nom EXACT. Détection avant curseur, navigation
 *  clavier (↑/↓/Entrée/Échap), insertion qui préserve le texte après le curseur. */
function ChampNoteMentions({
  value,
  onChange,
  membres,
  placeholder,
  disabled,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  membres: Membre[];
  placeholder?: string;
  disabled?: boolean | undefined;
  autoFocus?: boolean | undefined;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [requete, setRequete] = useState<string | null>(null);
  const [actif, setActif] = useState(0);

  const filtres =
    requete !== null
      ? membres
          .filter((m) => {
            const q = requete.toLowerCase();
            return m.nom.toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q);
          })
          .slice(0, 6)
      : [];

  function relire() {
    const el = ref.current;
    if (!el) return;
    setRequete(requeteMention(el.value.slice(0, el.selectionStart ?? el.value.length)));
    setActif(0);
  }

  function choisir(m: Membre) {
    const el = ref.current;
    if (!el) return;
    const pos = el.selectionStart ?? value.length;
    const avant = value.slice(0, pos).replace(/(^|\s)@[^\s@]*$/, `$1@${m.nom} `);
    const apres = value.slice(pos);
    const nouveau = avant + apres;
    onChange(nouveau);
    setRequete(null);
    // Replacer le curseur juste après « @Nom » inséré, une fois le state appliqué.
    requestAnimationFrame(() => {
      const e = ref.current;
      if (e) {
        e.focus();
        e.selectionStart = e.selectionEnd = avant.length;
      }
    });
  }

  return (
    <div className="note-mention-wrap">
      <textarea
        ref={ref}
        className="fiche-edition-champ"
        placeholder={placeholder}
        value={value}
        rows={2}
        disabled={disabled}
        // biome-ignore lint/a11y/noAutofocus: focus volontaire à l'ouverture de l'édition
        autoFocus={autoFocus}
        onChange={(e) => {
          onChange(e.currentTarget.value);
          relire();
        }}
        onClick={relire}
        onKeyUp={(e) => {
          // Flèches/Home/End déplacent le curseur sans onChange : on re-détecte.
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) relire();
        }}
        onKeyDown={(e) => {
          if (filtres.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActif((i) => (i + 1) % filtres.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActif((i) => (i - 1 + filtres.length) % filtres.length);
          } else if (e.key === 'Enter') {
            const m = filtres[actif];
            if (m) {
              e.preventDefault();
              choisir(m);
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setRequete(null);
          }
        }}
        onBlur={() => {
          // Laisse le mousedown du dropdown s'exécuter avant de fermer.
          setTimeout(() => setRequete(null), 120);
        }}
      />
      {filtres.length > 0 && (
        <ul className="note-mention-liste">
          {filtres.map((m, i) => (
            <li key={m.id}>
              <button
                type="button"
                className={`note-mention-item${i === actif ? ' actif' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choisir(m);
                }}
              >
                @{m.nom}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Modale de confirmation pour une action DESTRUCTIVE (barrière UX contre le clic
 *  accidentel ; la vraie barrière reste la proposition signée -> /action côté serveur).
 *  Pas de fermeture au clic sur l'overlay (trop implicite pour un destructif) ; Échap
 *  et « Annuler » suffisent. */
function ConfirmModale({
  titre,
  message,
  labelConfirmer,
  pending,
  onConfirmer,
  onAnnuler,
}: {
  titre: string;
  message: string;
  labelConfirmer: string;
  pending: boolean;
  onConfirmer: () => void;
  onAnnuler: () => void;
}) {
  useEffect(() => {
    const surEchap = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onAnnuler();
    };
    window.addEventListener('keydown', surEchap);
    return () => window.removeEventListener('keydown', surEchap);
  }, [pending, onAnnuler]);

  return (
    <div className="maj-overlay" role="dialog" aria-modal="true" aria-labelledby="confirm-titre">
      <div className="maj-modale">
        <h2 className="maj-titre" id="confirm-titre">
          {titre}
        </h2>
        <p className="maj-texte">{message}</p>
        <div className="confirm-actions">
          <button type="button" className="btn-secondaire" onClick={onAnnuler} disabled={pending}>
            Annuler
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={onConfirmer}
            disabled={pending}
            // biome-ignore lint/a11y/noAutofocus: focus initial sur l'action par défaut de la modale
            autoFocus
          >
            {pending ? 'Suppression…' : labelConfirmer}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Notes d'une fiche : chaque note est un OBJET daté séparé, regroupé ici, ÉDITABLE et
 * SUPPRIMABLE indépendamment, + un champ pour en AJOUTER une. Toute écriture passe par
 * onActionNote (proposition signée -> /action -> relecture de la fiche, règle 3).
 */
function NotesFiche({
  contactId,
  notes,
  notesErreur,
  onActionNote,
  desactive,
  connecte,
}: {
  contactId: string;
  notes: FicheContact['notes'];
  notesErreur?: boolean | undefined;
  onActionNote: (contactId: string, faire: () => Promise<{ jeton: string }>) => Promise<boolean>;
  desactive: boolean;
  connecte: boolean;
}) {
  const [ajout, setAjout] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editTexte, setEditTexte] = useState('');
  const [busy, setBusy] = useState(false);
  // Note dont la suppression est en attente de confirmation (modale).
  const [aSupprimer, setASupprimer] = useState<string | null>(null);
  // Membres OcéRM pour l'autocomplétion @ dans une note. Chargés UNIQUEMENT connecté
  // (/membres exige W1). L'échec ne bloque pas l'écriture : pas d'autocomplete, le
  // backend détecte quand même une mention tapée à la main.
  const [membres, setMembres] = useState<Membre[]>([]);
  useEffect(() => {
    if (!connecte) {
      setMembres([]);
      return;
    }
    let actif = true;
    void listerMembres()
      .then((m) => {
        if (actif) setMembres(m);
      })
      .catch(() => {
        if (actif) setMembres([]);
      });
    return () => {
      actif = false;
    };
  }, [connecte]);

  const ajouter = async () => {
    const t = ajout.trim();
    if (!t || busy) return;
    setBusy(true);
    const ok = await onActionNote(contactId, () => proposerAjoutNote(contactId, t));
    setBusy(false);
    if (ok) setAjout('');
  };
  const enregistrerEdit = async (noteId: string) => {
    const t = editTexte.trim();
    if (!t || busy) return;
    setBusy(true);
    const ok = await onActionNote(contactId, () => proposerModifNote(noteId, t));
    setBusy(false);
    if (ok) setEditId(null);
  };
  // Suppression destructive : on ne supprime qu'APRÈS confirmation dans la modale.
  const confirmerSuppression = async () => {
    if (busy || !aSupprimer) return;
    setBusy(true);
    const ok = await onActionNote(contactId, () => proposerSuppressionNote(aSupprimer));
    setBusy(false);
    if (ok) setASupprimer(null);
  };

  return (
    <div className="fiche-notes">
      <span className="fiche-notes-titre">Notes</span>
      {notes.length > 0
        ? notes.map((n) =>
            n.id && editId === n.id ? (
              <form
                key={n.id}
                className="fiche-note fiche-note-edition"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (n.id) void enregistrerEdit(n.id);
                }}
              >
                {/* Édition : textarea simple (pas d'autocomplete @). La notif de mention
                    n'est déclenchée QU'À l'ajout d'une note côté backend ; proposer @ ici
                    promettrait une notif qui ne partirait pas (cohérence §6). */}
                <textarea
                  className="fiche-edition-champ"
                  value={editTexte}
                  onChange={(e) => setEditTexte(e.currentTarget.value)}
                  rows={2}
                  disabled={busy || desactive}
                />
                <div className="fiche-edition-actions">
                  <button className="btn-principal" type="submit" disabled={busy || desactive}>
                    Enregistrer
                  </button>
                  <button
                    className="btn-secondaire"
                    type="button"
                    onClick={() => setEditId(null)}
                    disabled={busy}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            ) : (
              <div className="fiche-note" key={n.id ?? JSON.stringify([n.titre, n.contenu])}>
                <Markdown texte={n.contenu || n.titre} />
                <div className="fiche-note-bas">
                  {(n.auteur || n.date) && (
                    <span className="fiche-note-meta">
                      {n.auteur ? `par ${nomIdentite(n.auteur)}` : ''}
                      {n.auteur && n.date ? ', ' : ''}
                      {n.date ? `le ${dateNoteCourte(n.date)}` : ''}
                    </span>
                  )}
                  {n.id && (
                    <span className="fiche-note-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setEditId(n.id ?? null);
                          setEditTexte(n.contenu || n.titre);
                        }}
                        disabled={desactive}
                      >
                        Éditer
                      </button>
                      <button
                        type="button"
                        className="fiche-note-suppr"
                        onClick={() => n.id && setASupprimer(n.id)}
                        disabled={desactive}
                      >
                        Supprimer
                      </button>
                    </span>
                  )}
                </div>
              </div>
            ),
          )
        : notesErreur && (
            // Copy honnête (#17) : la lecture a échoué, on ne prétend pas « aucune note ».
            <p className="fiche-ligne-vide">Notes indisponibles pour le moment (réessaie).</p>
          )}
      {notes.length === 0 && !notesErreur && <p className="fiche-ligne-vide">Aucune note.</p>}

      <form
        className="fiche-note-ajout"
        onSubmit={(e) => {
          e.preventDefault();
          void ajouter();
        }}
      >
        <ChampNoteMentions
          value={ajout}
          onChange={setAjout}
          membres={membres}
          placeholder="Ajouter une note… (@ pour notifier un collègue)"
          disabled={busy || desactive}
        />
        <button
          className="btn-principal"
          type="submit"
          disabled={!ajout.trim() || busy || desactive}
        >
          {busy ? 'Enregistrement…' : 'Ajouter la note'}
        </button>
      </form>

      {aSupprimer && (
        <ConfirmModale
          titre="Supprimer la note ?"
          message="Cette note sera définitivement supprimée du carnet. Cette action est irréversible."
          labelConfirmer="Supprimer"
          pending={busy}
          onConfirmer={() => void confirmerSuppression()}
          onAnnuler={() => {
            if (!busy) setASupprimer(null);
          }}
        />
      )}
    </div>
  );
}

/** Une note retrouvée (recall) : texte RÉEL (canal serveur, jamais vu par le modèle),
 *  date, contact rattaché. Si la note est ORPHELINE (personId absent du brut serveur),
 *  un bouton ouvre un picker de contacts pour la rattacher (écriture signée -> /action). */
function LigneNoteRecall({
  note,
  onActionNote,
  desactive,
  connecte,
}: {
  note: NoteRecall;
  onActionNote: (contactId: string, faire: () => Promise<{ jeton: string }>) => Promise<boolean>;
  desactive: boolean;
  connecte: boolean;
}) {
  const [picker, setPicker] = useState(false);
  const [q, setQ] = useState('');
  const [resultats, setResultats] = useState<ContactResume[]>([]);
  const [busy, setBusy] = useState(false);
  // Nom du contact auquel on vient de rattacher (état réel post-action, pas supposé).
  const [rattacheeA, setRattacheeA] = useState<string | null>(null);

  useEffect(() => {
    if (!picker || !connecte || q.trim().length < 2) {
      setResultats([]);
      return;
    }
    let actif = true;
    const t = setTimeout(() => {
      void rechercher(q, { rapide: true })
        .then((r) => {
          if (actif) setResultats(r.slice(0, 6));
        })
        .catch(() => {
          if (actif) setResultats([]);
        });
    }, 200);
    return () => {
      actif = false;
      clearTimeout(t);
    };
  }, [picker, q, connecte]);

  const rattacher = async (c: ContactResume) => {
    if (busy || desactive || !note.id) return;
    const noteId = note.id;
    setBusy(true);
    const ok = await onActionNote(c.id, () => proposerRattachementNote(noteId, c.id));
    setBusy(false);
    if (ok) {
      setRattacheeA(c.nom);
      setPicker(false);
      setQ('');
    }
  };

  // Source de vérité du « orphelin » : le flag serveur `rattachable` (= zéro cible, ni
  // personne ni entreprise). Couvre multi-personnes (personne sans personId unique) ET
  // notes d'entreprise (sans targetPerson mais déjà liées) : aucune ne propose « Rattacher ».
  const orpheline = note.rattachable && !rattacheeA;
  const contactNom = rattacheeA ?? note.personne;

  return (
    <div className="note-recall">
      <Markdown texte={note.contenu || note.titre} />
      <div className="note-recall-bas">
        <span className="fiche-note-meta">
          {note.date ? `le ${dateNoteCourte(note.date)}` : ''}
          {note.date && contactNom ? ' · ' : ''}
          {contactNom ? `${rattacheeA ? 'rattachée à ' : ''}${contactNom}` : ''}
          {!note.date && !contactNom ? 'sans contact rattaché' : ''}
        </span>
        {orpheline && note.id && (
          <button
            type="button"
            className="note-recall-rattacher"
            onClick={() => setPicker((v) => !v)}
            disabled={desactive || busy}
          >
            {picker ? 'Annuler' : 'Rattacher à un contact'}
          </button>
        )}
      </div>
      {picker && orpheline && (
        <div className="note-recall-picker">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            placeholder="Chercher un contact…"
            disabled={busy}
            // biome-ignore lint/a11y/noAutofocus: focus à l'ouverture du picker de rattachement
            autoFocus
          />
          {resultats.length > 0 && (
            <ul className="note-mention-liste">
              {resultats.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="note-mention-item"
                    onClick={() => void rattacher(c)}
                    disabled={busy}
                  >
                    {c.nom}
                    {c.fonction || c.organisation
                      ? ` — ${[c.fonction, c.organisation].filter(Boolean).join(' · ')}`
                      : ''}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Carte des notes retrouvées par chercher_notes (recall). */
function CarteNotesRecall({
  notes,
  onActionNote,
  desactive,
  connecte,
}: {
  notes: NoteRecall[];
  onActionNote: (contactId: string, faire: () => Promise<{ jeton: string }>) => Promise<boolean>;
  desactive: boolean;
  connecte: boolean;
}) {
  return (
    <div className="carte note-recall-carte">
      <span className="fiche-notes-titre">Notes trouvées</span>
      {notes.map((n) => (
        <LigneNoteRecall
          key={n.id ?? JSON.stringify([n.titre, n.contenu, n.date])}
          note={n}
          onActionNote={onActionNote}
          desactive={desactive}
          connecte={connecte}
        />
      ))}
    </div>
  );
}

function CarteContact({
  c,
  onModifier,
  onActionNote,
  onPreparerSuppression,
  onSupprimer,
  desactive,
  connecte,
}: {
  c: FicheContact;
  onModifier: (
    contactId: string,
    champ: ChampFicheContactModifiable,
    valeur: string,
  ) => Promise<boolean>;
  onActionNote: (contactId: string, faire: () => Promise<{ jeton: string }>) => Promise<boolean>;
  onPreparerSuppression: (contact: FicheContact) => Promise<Proposition | null>;
  onSupprimer: (contact: FicheContact, proposition: Proposition) => Promise<boolean>;
  desactive: boolean;
  connecte: boolean;
}) {
  const [suppressionOuverte, setSuppressionOuverte] = useState(false);
  const [propositionSuppression, setPropositionSuppression] = useState<Proposition | null>(null);
  const [suppressionPreparation, setSuppressionPreparation] = useState(false);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const sousTitre = [c.fonction, c.organisation].filter(Boolean).join(' · ');
  // Appel : mobile en priorité, sinon le fixe. Compacté pour tel:/sms:.
  const numeroAppel = (c.mobile || c.telephone)?.replace(/[^\d+]/g, '') || null;
  // SMS : UNIQUEMENT le mobile (on n'envoie pas de SMS à une ligne fixe).
  const numeroSms = c.mobile?.replace(/[^\d+]/g, '') || null;
  const mail = c.email ? encodeURIComponent(c.email) : null;
  const emailsSecondaires = c.emailsSecondaires ?? [];
  const telephonesSupplementaires = c.telephonesSupplementaires ?? [];
  const ouvrirSuppression = async () => {
    if (suppressionPreparation || suppressionEnCours || desactive) return;
    setSuppressionPreparation(true);
    const proposition = await onPreparerSuppression(c);
    setSuppressionPreparation(false);
    if (!proposition) return;
    setPropositionSuppression(proposition);
    setSuppressionOuverte(true);
  };
  const confirmerSuppression = async () => {
    if (!propositionSuppression || suppressionEnCours || desactive) return;
    setSuppressionEnCours(true);
    const ok = await onSupprimer(c, propositionSuppression);
    if (ok) return;
    setSuppressionEnCours(false);
  };
  return (
    <div className="carte fiche-contact">
      <div className="fiche-tete">
        <strong className="fiche-nom">{c.nom}</strong>
        {sousTitre && <span className="fiche-soustitre">{sousTitre}</span>}
      </div>

      <TagsFiche
        contactId={c.id}
        tags={c.tags}
        tagsErreur={c.tagsErreur}
        onActionNote={onActionNote}
        desactive={desactive}
      />

      {/* Propriétaires (qui a importé/ajouté ce contact). Affiché seulement s'il y en a
          (§7) : un contact partagé montre toutes les personnes qui le possèdent. */}
      {c.proprietaires.length > 0 && (
        <p className="fiche-proprio">
          Ajouté par : {[...new Set(c.proprietaires.map(nomIdentite))].join(', ')}
        </p>
      )}

      {/* Actions seulement si le moyen de contact existe (sinon pas de bouton mort). */}
      {(numeroAppel || numeroSms || mail) && (
        <div className="fiche-actions">
          {numeroAppel && (
            <a className="action-btn appel" href={`tel:${numeroAppel}`}>
              📞 Appeler
            </a>
          )}
          {numeroSms && (
            <a className="action-btn sms" href={`sms:${numeroSms}`}>
              💬 SMS
            </a>
          )}
          {mail && (
            <a className="action-btn mail" href={`mailto:${mail}`}>
              ✉️ Mail
            </a>
          )}
        </div>
      )}

      {/* Tous les champs visibles : « non renseigné » rend explicite ce qui
          manque dans le CRM (à compléter), plutôt qu'une fiche qui semble vide. */}
      <div className="fiche-infos">
        {(
          [
            ['mobile', 'Mobile', c.mobile],
            ['telephone', 'Téléphone fixe', c.telephone],
            ['email', 'E-mail', c.email],
            ['organisation', 'Organisation', c.organisation],
            ['fonction', 'Fonction', c.fonction],
            ['ville', 'Ville', c.ville],
            ['adresse', 'Adresse', c.adresse],
          ] as Array<[ChampFicheContactModifiable, string, string | null]>
        ).map(([champ, libelle, valeur]) => (
          <LigneInfoEditable
            key={champ}
            contactId={c.id}
            champ={champ}
            libelle={libelle}
            valeur={valeur ?? null}
            onModifier={onModifier}
            desactive={desactive}
          />
        ))}
        {emailsSecondaires.map((email, index) => (
          <LigneInfo
            key={email.toLowerCase()}
            libelle={`E-mail secondaire${emailsSecondaires.length > 1 ? ` ${index + 1}` : ''}`}
            valeur={email}
          />
        ))}
        {telephonesSupplementaires.length > 0 && (
          <LigneInfo
            libelle="Téléphones supplémentaires"
            valeur={telephonesSupplementaires.join(', ')}
          />
        )}
      </div>

      {/* Notes de l'entreprise liée (ex. agences multi-sites) : rattachées à la
          Company, on les remonte ici pour qu'elles ne soient pas invisibles depuis
          la fiche personne (sinon l'utilisateur croit que les adresses sont perdues). */}
      {c.notesEntreprise && c.notesEntreprise.length > 0 && (
        <div className="fiche-notes fiche-notes-entreprise">
          <span className="fiche-notes-titre">
            Entreprise{c.organisation ? ` · ${c.organisation}` : ''}
          </span>
          {c.notesEntreprise.map((n) => (
            <div className="fiche-note" key={JSON.stringify(['ent', n.titre, n.contenu])}>
              <Markdown texte={n.contenu || n.titre} />
            </div>
          ))}
        </div>
      )}

      <NotesFiche
        contactId={c.id}
        notes={c.notes}
        notesErreur={c.notesErreur}
        onActionNote={onActionNote}
        desactive={desactive}
        connecte={connecte}
      />

      <a className="fiche-twenty" href={c.urlTwenty} target="_blank" rel="noreferrer">
        Ouvrir dans le CRM ↗
      </a>
      <div className="fiche-danger-zone">
        <button
          type="button"
          className="fiche-supprimer-btn"
          onClick={() => void ouvrirSuppression()}
          disabled={desactive || suppressionPreparation || suppressionEnCours}
        >
          {suppressionPreparation ? 'Préparation…' : 'Supprimer la fiche'}
        </button>
      </div>
      {suppressionOuverte && (
        <ConfirmModale
          titre={propositionSuppression?.description ?? `Supprimer la fiche « ${c.nom} »`}
          message="Cette fiche sera supprimée du CRM. Cette action est irréversible."
          labelConfirmer="Supprimer la fiche"
          pending={suppressionEnCours}
          onConfirmer={() => void confirmerSuppression()}
          onAnnuler={() => {
            if (!suppressionEnCours) {
              setSuppressionOuverte(false);
              setPropositionSuppression(null);
            }
          }}
        />
      )}
    </div>
  );
}

// Coordonnée(s) d'un contact, résolues côté serveur (B3) et affichées ici sans
// avoir transité par le modèle (donner_coordonnees). Boutons Appeler / SMS / Mail
// seulement si le moyen existe (pas de bouton mort, cf. CarteContact).
function CarteCoordonnees({ co }: { co: CoordonneesContact }) {
  const numeroAppel = (co.mobile || co.telephone)?.replace(/[^\d+]/g, '') || null;
  const numeroSms = co.mobile?.replace(/[^\d+]/g, '') || null;
  const mail = co.email ? encodeURIComponent(co.email) : null;
  const emailsSecondaires = co.emailsSecondaires ?? [];
  const telephonesSupplementaires = co.telephonesSupplementaires ?? [];
  const rien =
    !co.mobile &&
    !co.telephone &&
    !co.email &&
    emailsSecondaires.length === 0 &&
    telephonesSupplementaires.length === 0;
  return (
    <div className="carte fiche-contact">
      <div className="fiche-tete">
        <strong className="fiche-nom">{co.nom}</strong>
      </div>
      {(numeroAppel || numeroSms || mail) && (
        <div className="fiche-actions">
          {numeroAppel && (
            <a className="action-btn appel" href={`tel:${numeroAppel}`}>
              📞 Appeler
            </a>
          )}
          {numeroSms && (
            <a className="action-btn sms" href={`sms:${numeroSms}`}>
              💬 SMS
            </a>
          )}
          {mail && (
            <a className="action-btn mail" href={`mailto:${mail}`}>
              ✉️ Mail
            </a>
          )}
        </div>
      )}
      <div className="fiche-infos">
        <LigneInfo libelle="Mobile" valeur={co.mobile} />
        <LigneInfo libelle="Téléphone fixe" valeur={co.telephone} />
        <LigneInfo libelle="E-mail" valeur={co.email} />
        {emailsSecondaires.map((email, index) => (
          <LigneInfo
            key={email.toLowerCase()}
            libelle={`E-mail secondaire${emailsSecondaires.length > 1 ? ` ${index + 1}` : ''}`}
            valeur={email}
          />
        ))}
        {telephonesSupplementaires.length > 0 && (
          <LigneInfo
            libelle="Téléphones supplémentaires"
            valeur={telephonesSupplementaires.join(', ')}
          />
        )}
        {co.ville && <LigneInfo libelle="Ville" valeur={co.ville} />}
      </div>
      {rien && <p className="fiche-ligne-vide">Aucune coordonnée enregistrée pour ce contact.</p>}
    </div>
  );
}

function CarteProposition({
  p,
  onConfirmer,
  onOuvrirContact,
  desactive,
}: {
  p: Proposition;
  onConfirmer: (p: Proposition, options?: { noteComplementaire?: string }) => Promise<boolean>;
  onOuvrirContact: (contactId: string) => Promise<void>;
  desactive: boolean;
}) {
  const [etat, setEtat] = useState<'attente' | 'envoi' | 'confirme' | 'annule'>('attente');
  const [note, setNote] = useState('');
  const confirmationEnVol = useRef(false);
  const noteLibre = p.outil === 'creer_restaurant' || p.outil === 'creer_hotel';
  if (etat === 'confirme') return <p className="carte-ok">✓ Confirmé</p>;
  if (etat === 'annule') return <p className="carte-annule">Annulé</p>;
  return (
    <div className="carte carte-prop">
      <p className="carte-prop-titre">⚠ Modification à confirmer</p>
      <p className="carte-prop-desc">{p.description}</p>
      {noteLibre && (
        <label className="carte-prop-note">
          <span>Note (optionnelle)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            disabled={desactive || etat === 'envoi'}
            rows={3}
            maxLength={1000}
            placeholder="Ex. bon cari, terrasse agréable, à recommander..."
          />
        </label>
      )}
      <div className="carte-prop-actions">
        {p.contactId && (
          <button
            type="button"
            className="btn-secondaire"
            disabled={desactive || etat === 'envoi'}
            onClick={() => void onOuvrirContact(p.contactId as string)}
          >
            Voir la fiche
          </button>
        )}
        <button
          type="button"
          className="btn-principal"
          disabled={desactive || etat === 'envoi'}
          onClick={async () => {
            if (desactive) return;
            if (confirmationEnVol.current) return;
            confirmationEnVol.current = true;
            setEtat('envoi');
            try {
              // L'état « Confirmé » n'apparaît qu'après succès serveur réel.
              setEtat(
                (await onConfirmer(p, noteLibre ? { noteComplementaire: note } : undefined))
                  ? 'confirme'
                  : 'attente',
              );
            } finally {
              confirmationEnVol.current = false;
            }
          }}
        >
          {etat === 'envoi' ? 'En cours…' : 'Confirmer'}
        </button>
        <button
          type="button"
          className="btn-secondaire"
          disabled={desactive || etat === 'envoi'}
          onClick={() => setEtat('annule')}
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

function Saisie({
  onTexte,
  onNoteMention,
  onMention,
  onImage,
  onImages,
  onVocal,
  onChoisir,
  desactive,
  connecte,
}: {
  onTexte: (t: string) => void;
  onNoteMention: (contacts: ContactResume[], texte: string) => void;
  onMention: (membres: Membre[], texte: string) => void;
  onImage: (f: File) => void;
  onImages: (f: File[]) => void;
  onVocal: (blob: Blob) => void;
  onChoisir: (c: ContactResume) => void;
  desactive: boolean;
  connecte: boolean;
}) {
  const [texte, setTexte] = useState('');
  const [res, setRes] = useState<ContactResume[]>([]);
  const [rechercheOmniboxEtat, setRechercheOmniboxEtat] = useState<
    'idle' | 'loading' | 'empty' | 'error'
  >('idle');
  const [membres, setMembres] = useState<Membre[]>([]);
  const [mentionnes, setMentionnes] = useState<Membre[]>([]);
  // Commande « / » : contacts du carnet cités (mémorisés) + résultats d'autocomplete.
  const [contactsMentionnes, setContactsMentionnes] = useState<ContactResume[]>([]);
  const [contactsSlash, setContactsSlash] = useState<ContactResume[]>([]);
  const [contactsSlashRecherche, setContactsSlashRecherche] = useState(false);
  const [contactsSlashEtat, setContactsSlashEtat] = useState<
    'idle' | 'auth' | 'short' | 'loading' | 'empty' | 'error'
  >('idle');
  const [enreg, setEnreg] = useState(false);
  const champTexteRef = useRef<HTMLDivElement | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const morceaux = useRef<Blob[]>([]);

  // Membres du workspace (utilisateurs OcéRM) pour l'autocomplétion @ : chargés
  // UNIQUEMENT une fois connecté (l'endpoint /membres exige W1, et une mention sans
  // auth n'aboutirait pas). Évite un 401 inutile en boucle quand on est déconnecté.
  useEffect(() => {
    if (!connecte) {
      setMembres([]);
      return;
    }
    void listerMembres()
      .then(setMembres)
      .catch(() => setMembres([]));
  }, [connecte]);

  // Mention CONTACT « / » : la saisie se termine par "/<début>" -> on propose les
  // CONTACTS du carnet (autocomplete). Marche n'importe où dans la phrase (comme @).
  const contactSlash = detecterRequeteSlashFinale(
    texte,
    contactsMentionnes.map((c) => c.nom),
  );
  const requeteContactMention = contactSlash?.requeteRecherche ?? null;
  // Mention MEMBRE "@" : seulement si on n'est pas en train de citer un contact "/".
  const matchMention = requeteContactMention !== null ? null : /(^|\s)@([^\s@]*)$/.exec(texte);
  const peutChercherOmnibox =
    requeteContactMention === null && !/(^|\s)@/.test(texte) && texte.trim().length >= 2;
  const membresFiltres = matchMention
    ? membres
        .filter((m) => {
          // Trouvable par nom OU email : un membre identifié par email (nom vide côté
          // Twenty) reste mentionnable. Requête vide -> on montre tout le monde.
          const q = (matchMention[2] ?? '').toLowerCase();
          return m.nom.toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q);
        })
        .slice(0, 6)
    : [];

  // Autocomplete « / » : recherche les CONTACTS du carnet d'après le fragment après le /
  // (« /nass » -> Nassir). Gaté sur la connexion (le /search exige W1 ; inutile d'appeler
  // déconnecté -> évite un 401 en console).
  useEffect(() => {
    if (requeteContactMention === null) {
      setContactsSlash([]);
      setContactsSlashRecherche(false);
      setContactsSlashEtat('idle');
      return;
    }
    const q = requeteContactMention.trim();
    if (q.length < 2) {
      setContactsSlash([]);
      setContactsSlashRecherche(false);
      setContactsSlashEtat('short');
      return;
    }
    if (!connecte) {
      setContactsSlash([]);
      setContactsSlashRecherche(false);
      setContactsSlashEtat('auth');
      return;
    }
    setContactsSlash([]);
    setContactsSlashRecherche(true);
    setContactsSlashEtat('loading');
    let actif = true;
    const t = setTimeout(() => {
      void rechercher(q, { rapide: true })
        .then((r) => {
          if (!actif) return;
          setContactsSlash(r);
          setContactsSlashEtat(r.length > 0 ? 'idle' : 'empty');
        })
        .catch((e) => {
          if (!actif) return;
          setContactsSlash([]);
          setContactsSlashEtat('error');
          console.warn('Recherche slash impossible', e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (actif) setContactsSlashRecherche(false);
        });
    }, 200);
    return () => {
      actif = false;
      clearTimeout(t);
    };
  }, [requeteContactMention, connecte]);

  // Le composer n'est plus un input : il sort du parcours formulaire iOS. On
  // synchronise seulement les changements programmatiques pour ne pas faire sauter le curseur.
  useEffect(() => {
    const champ = champTexteRef.current;
    if (!champ) return;
    const actuel = texteDepuisSaisie(champ);
    if (actuel === texte && (texte !== '' || champ.innerHTML === '')) return;
    champ.textContent = textePourChampSaisie(texte);
    if (document.activeElement === champ) placerCurseurFin(champ);
  }, [texte]);

  // Omnibox : le champ unique sert AUSSI de recherche de contacts. À la frappe (≥2
  // car.), un dropdown propose des fiches (/search, rapide et gratuit) ; Entrée/↑
  // envoie le message à l'agent, cliquer une fiche l'ouvre. Un seul champ, deux usages
  // non ambigus (le dropdown ne capte jamais Entrée).
  useEffect(() => {
    // Pas de recherche omnibox si : on cite un contact « / », OU le texte contient une
    // mention @ (« @nas » ou « @Nassir Goulamaly » avec espace — ne doit pas relancer la
    // recherche, retour Kamil), OU moins de 2 caractères.
    if (!peutChercherOmnibox) {
      setRes([]);
      setRechercheOmniboxEtat('idle');
      return;
    }
    setRes([]);
    setRechercheOmniboxEtat('loading');
    // Garde anti-race : si `texte` change (frappe, envoi, choix d'une fiche -> setTexte(''))
    // pendant qu'un /search est en vol, on ignore sa réponse tardive. Sinon des résultats
    // périmés réapparaîtraient sous un champ vidé/différent (revue Codex).
    let actif = true;
    const t = setTimeout(() => {
      void rechercher(texte, { rapide: true })
        .then((r) => {
          if (!actif) return;
          setRes(r);
          setRechercheOmniboxEtat(r.length > 0 ? 'idle' : 'empty');
        })
        .catch(() => {
          if (!actif) return;
          setRes([]);
          setRechercheOmniboxEtat('error');
        });
    }, 250);
    return () => {
      actif = false;
      clearTimeout(t);
    };
  }, [texte, peutChercherOmnibox]);

  function choisir(c: ContactResume) {
    onChoisir(c);
    setTexte('');
    setRes([]);
    setRechercheOmniboxEtat('idle');
  }

  // Choix d'un CONTACT depuis le dropdown « / » : remplace le fragment "/<début>" par
  // "/Nom " et mémorise le contact (cible de la note à l'envoi).
  function choisirContactMention(c: ContactResume) {
    setTexte((t) => {
      const mention = detecterRequeteSlashFinale(
        t,
        contactsMentionnes.map((contact) => contact.nom),
      );
      if (!mention) return t;
      return `${t.slice(0, mention.debut)}/${c.nom} ${t.slice(mention.fin)}`;
    });
    setContactsMentionnes((prev) => (prev.some((x) => x.id === c.id) ? prev : [...prev, c]));
    setContactsSlash([]);
    setContactsSlashEtat('idle');
    setRes([]);
    setRechercheOmniboxEtat('idle');
  }

  // Choix d'un membre depuis le dropdown @ : remplace le fragment "@<début>" par
  // "@Nom " et mémorise le membre (cible de la notif à l'envoi).
  function choisirMembre(m: Membre) {
    setTexte((t) => t.replace(/(^|\s)@[^\s@]*$/, `$1@${m.nom} `));
    setMentionnes((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    setRes([]);
    setRechercheOmniboxEtat('idle');
  }

  function ouvrirSelecteurFichiers({
    accept,
    capture,
    multiple,
    onSelection,
  }: {
    accept: string;
    capture?: string;
    multiple?: boolean;
    onSelection: (files: File[]) => void;
  }) {
    if (desactive) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    if (capture) input.setAttribute('capture', capture);
    if (multiple) input.multiple = true;
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');
    input.style.position = 'fixed';
    input.style.left = '-10000px';
    input.style.top = '0';
    input.style.width = '1px';
    input.style.height = '1px';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';

    let nettoye = false;
    let minuteur: number | undefined;
    const nettoyer = () => {
      if (nettoye) return;
      nettoye = true;
      if (minuteur !== undefined) window.clearTimeout(minuteur);
      window.removeEventListener('focus', nettoyerApresRetour);
      document.removeEventListener('visibilitychange', surVisibilite);
      input.remove();
    };
    const nettoyerApresRetour = () => {
      window.setTimeout(nettoyer, 400);
    };
    const surVisibilite = () => {
      if (document.visibilityState === 'visible') nettoyerApresRetour();
    };
    input.addEventListener(
      'change',
      () => {
        const files = Array.from(input.files ?? []);
        if (files.length > 0) onSelection(files);
        nettoyer();
      },
      { once: true },
    );
    input.addEventListener('cancel', nettoyer, { once: true });
    window.addEventListener('focus', nettoyerApresRetour, { once: true });
    document.addEventListener('visibilitychange', surVisibilite);
    minuteur = window.setTimeout(nettoyer, 120000);
    document.body.append(input);
    input.click();
  }

  function lireChampTexte(champ: HTMLDivElement) {
    const brut = champ.textContent ?? '';
    const contenu = texteDepuisSaisie(champ);
    if (/[\r\n]/.test(brut) || (contenu === '' && champ.innerHTML !== '')) {
      champ.textContent = textePourChampSaisie(contenu);
      placerCurseurFin(champ);
    }
    setTexte(contenu);
  }

  function surBeforeInput(e: FormEvent<HTMLDivElement>) {
    const natif = e.nativeEvent as InputEvent;
    if (desactive) {
      e.preventDefault();
      return;
    }
    if (natif.inputType === 'insertParagraph' || natif.inputType === 'insertLineBreak') {
      e.preventDefault();
      if (!natif.isComposing) envoyer();
    }
  }

  function surColler(e: ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    if (desactive) return;
    const champ = e.currentTarget;
    const texteColle = normaliserTexteSaisie(e.clipboardData.getData('text/plain'));
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      champ.textContent = `${champ.textContent ?? ''}${texteColle}`;
      placerCurseurFin(champ);
      lireChampTexte(champ);
      return;
    }
    const range = selection.getRangeAt(0);
    if (!champ.contains(range.commonAncestorContainer)) {
      champ.textContent = `${champ.textContent ?? ''}${texteColle}`;
      placerCurseurFin(champ);
      lireChampTexte(champ);
      return;
    }
    range.deleteContents();
    range.insertNode(document.createTextNode(texteColle));
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    lireChampTexte(champ);
  }

  function surPointerDownChamp(e: PointerEvent<HTMLDivElement>) {
    if (desactive || document.activeElement === e.currentTarget) return;
    if (e.button !== 0) return;
    e.preventDefault();
    focusSaisieSansScroll(e.currentTarget, { x: e.clientX, y: e.clientY });
  }

  function envoyer() {
    const t = texte.trim();
    if (!t || desactive) return;
    setTexte('');
    setRes([]);
    setRechercheOmniboxEtat('idle');
    setContactsSlash([]);
    setContactsSlashEtat('idle');
    // Contacts du carnet cités via « / » -> note/maj sur ces contacts (pas l'agent).
    // Frontière (pas un simple includes) : « /Ann » ne doit pas matcher « /Anna Dupont ».
    const contactsCites = contactsMentionnes.filter((c) =>
      new RegExp(`/${c.nom.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\p{L}\\p{N}])`, 'u').test(t),
    );
    if (contactsCites.length > 0) {
      setContactsMentionnes([]);
      setMentionnes([]);
      onNoteMention(contactsCites, t);
      return;
    }
    // Membres OcéRM mentionnés via « @ » -> notif push (pas un message à l'agent).
    const cibles = mentionnes.filter((m) => t.includes(`@${m.nom}`));
    setMentionnes([]);
    if (cibles.length > 0) onMention(cibles, t);
    else onTexte(t);
  }

  async function basculerAudio() {
    if (enreg) {
      recRef.current?.stop();
      return;
    }
    try {
      const flux = await navigator.mediaDevices.getUserMedia({ audio: true });
      // iPhone/Safari n'enregistre pas en webm : on choisit un format réellement
      // supporté (webm sur Android/desktop, mp4/AAC sur iOS) et on garde le type
      // réel pour que la transcription reçoive le bon format (sinon « rien entendu »).
      const formats = ['audio/webm', 'audio/mp4', 'audio/aac'];
      const supporte =
        typeof MediaRecorder.isTypeSupported === 'function'
          ? formats.find((f) => MediaRecorder.isTypeSupported(f))
          : undefined;
      const rec = supporte
        ? new MediaRecorder(flux, { mimeType: supporte })
        : new MediaRecorder(flux);
      morceaux.current = [];
      rec.ondataavailable = (e) => morceaux.current.push(e.data);
      rec.onstop = () => {
        for (const piste of flux.getTracks()) piste.stop();
        setEnreg(false);
        // Le mémo vocal devient directement un message : envoyé, analysé côté
        // serveur, puis le son est jeté (jamais conservé). Pas de transcription
        // à éditer à la main. On conserve le type RÉEL de l'enregistreur.
        const blob = new Blob(morceaux.current, { type: rec.mimeType || supporte || 'audio/webm' });
        morceaux.current = [];
        onVocal(blob);
      };
      recRef.current = rec;
      rec.start();
      setEnreg(true);
    } catch {
      /* micro refusé */
    }
  }

  const afficheSlash =
    (contactsSlashRecherche || contactsSlashEtat !== 'idle' || contactsSlash.length > 0) &&
    !desactive;
  const afficheMembres = Boolean(matchMention && membresFiltres.length > 0 && !desactive);
  const afficheRecherche =
    peutChercherOmnibox && (res.length > 0 || rechercheOmniboxEtat !== 'idle') && !desactive;
  const afficheResultats = afficheSlash || afficheMembres || afficheRecherche;

  return (
    <>
      {afficheResultats && (
        <div className="saisie-overlays">
          {afficheSlash && (
            <ul className="recherche-res omnibox-res slash-res" aria-label="Contacts à mentionner">
              {contactsSlashRecherche && (
                <li>
                  <div className="slash-loading" role="status" aria-live="polite">
                    <span className="mini-spinner" aria-hidden="true" />
                    <span>Recherche…</span>
                  </div>
                </li>
              )}
              {!contactsSlashRecherche && contactsSlashEtat === 'auth' && (
                <li>
                  <div className="slash-loading slash-status" role="status" aria-live="polite">
                    <span>Connexion au carnet en cours…</span>
                  </div>
                </li>
              )}
              {!contactsSlashRecherche && contactsSlashEtat === 'short' && (
                <li>
                  <div className="slash-loading slash-status" role="status" aria-live="polite">
                    <span>Tape au moins 2 lettres</span>
                  </div>
                </li>
              )}
              {!contactsSlashRecherche && contactsSlashEtat === 'empty' && (
                <li>
                  <div className="slash-loading slash-status" role="status" aria-live="polite">
                    <span>Aucun contact trouvé</span>
                  </div>
                </li>
              )}
              {!contactsSlashRecherche && contactsSlashEtat === 'error' && (
                <li>
                  <div className="slash-loading slash-status" role="status" aria-live="polite">
                    <span>Recherche impossible</span>
                  </div>
                </li>
              )}
              {!contactsSlashRecherche &&
                contactsSlashEtat === 'idle' &&
                contactsSlash.slice(0, 6).map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => choisirContactMention(c)}>
                      <span className="slash-indic" aria-hidden="true">
                        /
                      </span>
                      <span className="slash-texte">
                        <strong>{c.nom}</strong>
                        <span>{[c.fonction, c.organisation].filter(Boolean).join(' · ')}</span>
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          )}
          {afficheMembres && (
            <ul className="recherche-res omnibox-res">
              {membresFiltres.map((m) => (
                <li key={m.id}>
                  <button type="button" onClick={() => choisirMembre(m)}>
                    @{m.nom}
                    {m.email ? ` · ${m.email}` : ''}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {afficheRecherche && (
            <ul className="recherche-res omnibox-res" aria-label="Contacts trouvés">
              {rechercheOmniboxEtat === 'loading' && (
                <li>
                  <div className="slash-loading" role="status" aria-live="polite">
                    <span className="mini-spinner" aria-hidden="true" />
                    <span>Recherche…</span>
                  </div>
                </li>
              )}
              {rechercheOmniboxEtat === 'empty' && (
                <li>
                  <div className="slash-loading slash-status" role="status" aria-live="polite">
                    <span>Aucun contact trouvé</span>
                  </div>
                </li>
              )}
              {rechercheOmniboxEtat === 'error' && (
                <li>
                  <div className="slash-loading slash-status" role="status" aria-live="polite">
                    <span>Recherche impossible</span>
                  </div>
                </li>
              )}
              {rechercheOmniboxEtat === 'idle' &&
                res.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => choisir(c)}>
                      <strong>{c.nom}</strong>
                      <span>{[c.fonction, c.organisation].filter(Boolean).join(' · ')}</span>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
      <footer className="saisie">
        <button
          type="button"
          className="btn-icone"
          title="Prendre une photo de carte"
          aria-label="Prendre une photo de carte"
          disabled={desactive}
          onClick={() =>
            ouvrirSelecteurFichiers({
              accept: 'image/*',
              capture: 'environment',
              onSelection: (fs) => {
                const f = fs[0];
                if (f) onImage(f);
              },
            })
          }
        >
          <IconeCamera />
        </button>
        <button
          type="button"
          className="btn-icone"
          title="Importer une ou plusieurs photos"
          aria-label="Importer une ou plusieurs photos"
          disabled={desactive}
          onClick={() =>
            ouvrirSelecteurFichiers({
              accept: 'image/*,.heic,.heif',
              multiple: true,
              onSelection: (fs) => {
                if (fs.length > 0) onImages(fs);
              },
            })
          }
        >
          <IconeImage />
        </button>
        <button
          type="button"
          className={`btn-icone ${enreg ? 'enreg' : ''}`}
          title="Dicter"
          onClick={() => void basculerAudio()}
        >
          {enreg ? <IconeStop /> : <IconeMicro />}
        </button>
        {/* biome-ignore lint/a11y/useSemanticElements: input/textarea déclenchent la barre formulaire iOS que ce composer doit éviter. */}
        <div
          ref={champTexteRef}
          className="saisie-texte"
          contentEditable={desactive ? false : 'plaintext-only'}
          suppressContentEditableWarning
          role="textbox"
          aria-label="Message"
          aria-multiline="false"
          aria-disabled={desactive}
          aria-placeholder={enreg ? 'Enregistrement…' : 'Message…'}
          data-placeholder={enreg ? 'Enregistrement…' : 'Message…'}
          tabIndex={desactive ? -1 : 0}
          inputMode="text"
          spellCheck={false}
          enterKeyHint="send"
          onPointerDown={surPointerDownChamp}
          onBeforeInput={surBeforeInput}
          onInput={(e) => lireChampTexte(e.currentTarget)}
          onKeyDown={(e) => {
            if (desactive) {
              e.preventDefault();
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!e.nativeEvent.isComposing) envoyer();
            }
          }}
          onPaste={surColler}
        />
        <button
          type="button"
          className="btn-envoyer"
          onClick={envoyer}
          disabled={desactive}
          aria-label="Envoyer"
        >
          <IconeEnvoi />
        </button>
      </footer>
    </>
  );
}

/** Événement `beforeinstallprompt` (non typé dans la lib standard). */
interface EvenementInstall extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Bouton « Installer l'app » (PWA sur l'écran d'accueil).
 * - Android/Chrome/Edge : capte `beforeinstallprompt` et déclenche l'install native.
 * - iPhone/Safari : aucune API d'install -> on affiche les instructions
 *   (Partager -> « Sur l'écran d'accueil »).
 * - Déjà installée (lancée en standalone) : on n'affiche rien.
 */
function BoutonInstaller() {
  const [promptInstall, setPromptInstall] = useState<EvenementInstall | null>(null);
  const [installee, setInstallee] = useState(false);
  const [aideIos, setAideIos] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    if (standalone) {
      setInstallee(true);
      return;
    }
    const surPrompt = (e: Event) => {
      e.preventDefault(); // on garde la main : install au clic du bouton
      setPromptInstall(e as EvenementInstall);
    };
    const surInstall = () => setInstallee(true);
    window.addEventListener('beforeinstallprompt', surPrompt);
    window.addEventListener('appinstalled', surInstall);
    return () => {
      window.removeEventListener('beforeinstallprompt', surPrompt);
      window.removeEventListener('appinstalled', surInstall);
    };
  }, []);

  const estIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  // Rien à proposer : déjà installée, ou plateforme sans install possible
  // (desktop sans prompt natif déclenché, etc.).
  if (installee || (!promptInstall && !estIos)) return null;

  async function installer() {
    if (promptInstall) {
      await promptInstall.prompt();
      await promptInstall.userChoice;
      setPromptInstall(null);
    } else {
      setAideIos(true); // iOS : pas d'API, on guide à la main
    }
  }

  return (
    <>
      <button
        type="button"
        className="btn-installer"
        onClick={() => void installer()}
        title="Installer OcéRM sur ton écran d'accueil"
        aria-label="Installer OcéRM sur ton écran d'accueil"
      >
        <IconeInstaller size={18} />
        <span className="btn-texte">Installer</span>
      </button>
      {aideIos && (
        <div
          className="maj-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="install-titre"
        >
          <div className="maj-modale">
            <h2 id="install-titre" className="maj-titre">
              Installer OcéRM sur iPhone
            </h2>
            <ol className="install-etapes">
              <li>
                Appuie sur <strong>Partager</strong> (l'icône carré avec une flèche, en bas de
                Safari)
              </li>
              <li>
                Descends et choisis <strong>« Sur l'écran d'accueil »</strong>
              </li>
              <li>
                Appuie sur <strong>Ajouter</strong> en haut à droite
              </li>
            </ol>
            <button type="button" className="maj-bouton" onClick={() => setAideIos(false)}>
              Compris
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const LIBELLE_STATUT: Record<string, string> = {
  nouveau: '🟢 Nouveau',
  a_completer: '🟦 À compléter',
  deja: '⚪ Déjà à jour',
  conflit: '🟧 Conflit',
};

/** Import du carnet du téléphone. Deux chemins (§7, affichage conditionnel) : Contact
 *  Picker natif sur Android, upload .vcf sur iPhone (Contact Picker absent de WebKit).
 *  Les deux convergent vers le MÊME aperçu serveur (un seul parseur/dédup, §5). Flux :
 *  aperçu LECTURE SEULE (statut + diff par contact) -> sélection (on n'importe que les
 *  cochés, minimisation) -> confirmation -> résultat. */
/** Écran de choix de la source d'import (picker natif Android vs upload .vcf + aide iOS). */
function ImportChoix({
  picker,
  onPicker,
  onImporterFichier,
  info,
}: {
  picker: boolean;
  onPicker: () => void;
  onImporterFichier: () => void;
  info: string | null;
}) {
  return (
    <div className="import-choix">
      {picker ? (
        <>
          <button type="button" className="import-cta" onClick={onPicker}>
            Choisir dans mes contacts
          </button>
          <button type="button" className="import-lien" onClick={onImporterFichier}>
            ou importer un fichier .vcf / .vcard
          </button>
        </>
      ) : (
        <>
          <button type="button" className="import-cta" onClick={onImporterFichier}>
            Importer un fichier .vcf / .vcard
          </button>
          <div className="import-aide">
            <p>{IMPORT_VCARD_AIDE.intro}</p>
            <p>
              <strong>{IMPORT_VCARD_AIDE.iphoneTitre}</strong> : {IMPORT_VCARD_AIDE.iphoneEtapes}{' '}
              <strong>{IMPORT_VCARD_AIDE.iphonePartager}</strong> →{' '}
              <strong>{IMPORT_VCARD_AIDE.iphoneEnregistrer}</strong>, {IMPORT_VCARD_AIDE.iphoneFin}
            </p>
            <p>
              <strong>{IMPORT_VCARD_AIDE.plusieursTitre}</strong> :{' '}
              {IMPORT_VCARD_AIDE.plusieursDebut} <strong>{IMPORT_VCARD_AIDE.mac}</strong>,{' '}
              {IMPORT_VCARD_AIDE.plusieursEtapes} <strong>{IMPORT_VCARD_AIDE.exporterVcard}</strong>
              {'. '}
              {IMPORT_VCARD_AIDE.icloudIntro} <strong>{IMPORT_VCARD_AIDE.icloud}</strong>{' '}
              {IMPORT_VCARD_AIDE.icloudEtapes} <strong>{IMPORT_VCARD_AIDE.exporterVcard}</strong>
              {'.'}
            </p>
          </div>
        </>
      )}
      {info && (
        <p className="import-info" role="alert">
          {info}
        </p>
      )}
    </div>
  );
}

/** Aperçu (lecture seule) d'un import : résumé par statut + liste à cocher + mini-diff
 *  des conflits + actions. État (sélection) tenu par BoutonImport, vue pure ici. */
function ImportApercu({
  apercu,
  selection,
  onBasculer,
  onToutCocher,
  onQueNouveaux,
  onAucun,
  onConfirmer,
  onAnnuler,
  info,
}: {
  apercu: ApercuImport;
  selection: Set<number>;
  onBasculer: (i: number) => void;
  onToutCocher: () => void;
  onQueNouveaux: () => void;
  onAucun: () => void;
  onConfirmer: () => void;
  onAnnuler: () => void;
  info: string | null;
}) {
  return (
    <>
      <p className="import-resume">
        {apercu.parStatut.nouveau} nouveau(x) · {apercu.parStatut.a_completer} à compléter ·{' '}
        {apercu.parStatut.deja} déjà à jour · {apercu.parStatut.conflit} conflit(s)
        {apercu.rejetes ? ` · ${apercu.rejetes} ignoré(s)` : ''}
      </p>
      <div className="import-selecteurs">
        <button type="button" className="import-lien" onClick={onToutCocher}>
          Tout
        </button>
        <button type="button" className="import-lien" onClick={onQueNouveaux}>
          Que les nouveaux
        </button>
        <button type="button" className="import-lien" onClick={onAucun}>
          Aucun
        </button>
      </div>
      <ul className="import-liste">
        {apercu.contacts.map((c) => {
          const emailResume =
            c.email && c.emailsSecondaires?.length
              ? `${c.email} (+${c.emailsSecondaires.length})`
              : c.email;
          const telephoneResume =
            c.telephonesSupplementaires?.length && (c.mobile || c.telephone)
              ? `${c.mobile ?? c.telephone} (+${c.telephonesSupplementaires.length})`
              : (c.mobile ?? c.telephone);
          return (
            <li key={c.index} className="import-ligne">
              <label className="import-coche">
                <input
                  type="checkbox"
                  checked={selection.has(c.index)}
                  onChange={() => onBasculer(c.index)}
                />
                <span className="import-nom">{c.nom}</span>
              </label>
              <span className={`import-badge import-badge-${c.statut}`}>
                {LIBELLE_STATUT[c.statut] ?? c.statut}
              </span>
              {(c.organisation || emailResume || telephoneResume) && (
                <span className="import-sous">
                  {[c.organisation, emailResume ?? telephoneResume].filter(Boolean).join(' · ')}
                </span>
              )}
              {c.infosImport?.length ? (
                <span className="import-sous">À importer : {c.infosImport.join(', ')}</span>
              ) : null}
              {c.conflits.length > 0 && (
                <ul className="import-diff">
                  {c.conflits.map((d) => (
                    <li key={d.champ}>
                      <strong>{d.champ}</strong> : carnet « {d.actuel} » gardé, import « {d.entrant}{' '}
                      » ignoré
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      {info && (
        <p className="import-info" role="alert">
          {info}
        </p>
      )}
      <div className="import-actions">
        <button
          type="button"
          className="import-cta"
          disabled={selection.size === 0}
          onClick={onConfirmer}
        >
          Importer{selection.size > 0 ? ` (${selection.size})` : ''}
        </button>
        <button type="button" className="import-lien" onClick={onAnnuler}>
          Annuler
        </button>
      </div>
    </>
  );
}

function ImportChargement({ texte }: { texte: string }) {
  return (
    <div className="import-chargement" role="status" aria-live="polite">
      <span className="mini-spinner" aria-hidden="true" />
      <span>{texte}</span>
    </div>
  );
}

function BoutonImport() {
  const [ouvert, setOuvert] = useState(false);
  const [etat, setEtat] = useState<'choix' | 'lecture' | 'apercu' | 'envoi' | 'fait'>('choix');
  const [apercu, setApercu] = useState<ApercuImport | null>(null);
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [texteVcf, setTexteVcf] = useState('');
  const [resultat, setResultat] = useState<ResultatImport | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const champRef = useRef<HTMLInputElement>(null);
  const picker = pickerContactsDispo();

  const fermer = () => {
    setOuvert(false);
    setEtat('choix');
    setApercu(null);
    setSelection(new Set());
    setTexteVcf('');
    setResultat(null);
    setInfo(null);
  };

  const lancerApercu = (texte: string) => {
    setTexteVcf(texte);
    setEtat('lecture');
    setInfo('Analyse du fichier…');
    apercuImportVcard(texte)
      .then((a) => {
        setApercu(a);
        // Pré-sélection : nouveau + à compléter + conflit cochés ; déjà à jour décoché
        // (l'import y serait un no-op). L'utilisateur ajuste.
        setSelection(new Set(a.contacts.filter((c) => c.statut !== 'deja').map((c) => c.index)));
        setEtat('apercu');
        if (a.importables === 0) setInfo('Aucun contact exploitable.');
      })
      .catch((err: unknown) => {
        setEtat('choix');
        setInfo(`Lecture impossible (${err instanceof Error ? err.message : 'inconnu'}).`);
      });
  };

  const onFichier = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setEtat('lecture');
    setInfo('Lecture du fichier…');
    f.text()
      .then(lancerApercu)
      .catch(() => {
        setEtat('choix');
        setInfo('Fichier illisible.');
      });
  };

  const onPicker = () => {
    setInfo(null);
    choisirContactsTelephone()
      .then((texte) => {
        if (texte) lancerApercu(texte);
      })
      .catch((err: unknown) =>
        setInfo(`Sélection impossible (${err instanceof Error ? err.message : 'inconnu'}).`),
      );
  };

  const basculer = (i: number) =>
    setSelection((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });
  const choisirSelection = (mode: ModeSelectionImport) => {
    if (apercu) setSelection(selectionImportPour(apercu, mode));
  };

  const confirmer = () => {
    if (!apercu || selection.size === 0) return;
    setEtat('envoi');
    setInfo(null);
    confirmerImportVcard(texteVcf, [...selection])
      .then((r) => {
        setResultat(r);
        setEtat('fait');
      })
      .catch((err: unknown) => {
        setEtat('apercu');
        setInfo(`Import impossible (${err instanceof Error ? err.message : 'inconnu'}).`);
      });
  };

  const modaleImport = ouvert ? (
    <div className="import-overlay">
      <div className="import-modale" role="dialog" aria-modal="true" aria-label="Importer">
        <div className="import-entete">
          <strong>Importer mes contacts</strong>
          <button type="button" className="import-fermer" onClick={fermer} aria-label="Fermer">
            ✕
          </button>
        </div>

        {etat === 'choix' && (
          <ImportChoix
            picker={picker}
            onPicker={onPicker}
            onImporterFichier={() => champRef.current?.click()}
            info={info}
          />
        )}

        {etat === 'lecture' && (
          <ImportChargement texte={info ?? 'Lecture et analyse du fichier…'} />
        )}

        {etat === 'apercu' && apercu && (
          <ImportApercu
            apercu={apercu}
            selection={selection}
            onBasculer={basculer}
            onToutCocher={() => choisirSelection('tous')}
            onQueNouveaux={() => choisirSelection('nouveaux')}
            onAucun={() => choisirSelection('aucun')}
            onConfirmer={confirmer}
            onAnnuler={fermer}
            info={info}
          />
        )}

        {etat === 'envoi' && <ImportChargement texte="Import en cours…" />}

        {etat === 'fait' && resultat && (
          <div className="import-resultat" role="alert">
            <p className="import-resultat-titre">✓ Import terminé</p>
            <ul>
              <li>{resultat.importes} contact(s) sélectionné(s) traité(s)</li>
              {resultat.crees > 0 && <li>{resultat.crees} fiche(s) créée(s)</li>}
              {resultat.completes > 0 && <li>{resultat.completes} complétée(s)</li>}
              {resultat.deja > 0 && <li>{resultat.deja} déjà à jour</li>}
              {resultat.conflits.length > 0 && (
                <li>{resultat.conflits.length} en conflit (valeurs gardées)</li>
              )}
              {resultat.erreurs > 0 && <li>{resultat.erreurs} en erreur</li>}
              {resultat.notesImportEchouees ? (
                <li>{resultat.notesImportEchouees} note(s) d'import non enregistrée(s)</li>
              ) : null}
              {resultat.proprietairesEchoues ? (
                <li>
                  {resultat.proprietairesEchoues} propriétaire(s) non ajouté(s) automatiquement
                </li>
              ) : null}
              {resultat.rejetes > 0 && <li>{resultat.rejetes} ignoré(s) (illisibles)</li>}
            </ul>
            <button type="button" className="import-cta" onClick={fermer}>
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <span className="cloche-bloc">
      <button
        type="button"
        className="cloche"
        title="Importer mes contacts"
        onClick={() => {
          setOuvert(true);
          setEtat('choix');
        }}
      >
        <IconeContacts size={20} />
      </button>
      <input
        ref={champRef}
        type="file"
        accept=".vcf,.vcard,text/vcard,text/x-vcard"
        hidden
        onChange={onFichier}
      />
      {modaleImport && createPortal(modaleImport, document.body)}
    </span>
  );
}

function extraitNotification(texte: string): string {
  const propre = texte.replace(/\s+/g, ' ').trim();
  return propre.length > 120 ? `${propre.slice(0, 117)}...` : propre;
}

function dateRelative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diff = date.getTime() - Date.now();
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat('fr-FR', { numeric: 'auto', style: 'short' });
  if (abs < 45_000) return "à l'instant";
  if (abs < 45 * 60_000) return rtf.format(Math.round(diff / 60_000), 'minute');
  if (abs < 22 * 3_600_000) return rtf.format(Math.round(diff / 3_600_000), 'hour');
  if (abs < 26 * 86_400_000) return rtf.format(Math.round(diff / 86_400_000), 'day');
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function contactIdDepuisLien(lien: string | null): string | null {
  if (!lien?.startsWith('/contact/')) return null;
  const brut = lien.slice('/contact/'.length).split(/[/?#]/)[0];
  if (!brut) return null;
  try {
    return decodeURIComponent(brut);
  } catch {
    return brut;
  }
}

/**
 * Cloche du header = centre de notifications : badge de non-lues + panneau déroulant
 * (liste lu/non-lu, vignette par type, clic -> marque lu + ouvre la fiche liée). Garde
 * l'activation push (web-push) comme une action dans le panneau.
 */
function BoutonNotifications({
  connecte,
  onOuvrirContact,
}: {
  connecte: boolean;
  onOuvrirContact: (contactId: string) => Promise<void>;
}) {
  const [etat, setEtat] = useState<NotificationPermission | 'non-supporte'>(etatAutorisation());
  const [ouvert, setOuvert] = useState(false);
  const [notifications, setNotifications] = useState<NotificationOcerm[]>([]);
  const [nonLues, setNonLues] = useState(0);
  const [prochainAvant, setProchainAvant] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const rafraichirCompteur = useCallback(() => {
    if (!estConnecte()) {
      setNonLues(0);
      return;
    }
    void compteurNotifications()
      // Garde : si on s'est déconnecté pendant la requête, ne pas réafficher le compteur
      // de l'ancien compte (revue Codex, confidentialité).
      .then((r) => {
        if (estConnecte()) setNonLues(r.nonLues);
      })
      .catch(() => {});
  }, []);

  const charger = useCallback(async (avant?: string | null) => {
    if (!estConnecte()) {
      setNotifications([]);
      setNonLues(0);
      setProchainAvant(null);
      return;
    }
    setChargement(true);
    setInfo(null);
    try {
      const page = await listerNotifications(20, avant);
      // Déconnecté pendant la requête : on ne réaffiche pas l'état de l'ancien compte.
      if (!estConnecte()) return;
      setNotifications((actuelles) =>
        avant ? [...actuelles, ...page.notifications] : page.notifications,
      );
      setNonLues(page.nonLues);
      setProchainAvant(page.prochainAvant);
    } catch {
      setInfo('Notifications indisponibles.');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    // Déconnexion : on vide l'état (badge + liste + panneau) pour ne pas exposer
    // d'anciennes notifs (revue Codex). Connexion (y compris restauration async) :
    // on rafraîchit le compteur, puis à chaque retour de focus fenêtre.
    if (!connecte) {
      setNonLues(0);
      setNotifications([]);
      setProchainAvant(null);
      setOuvert(false);
      return;
    }
    rafraichirCompteur();
    const surFocus = () => rafraichirCompteur();
    window.addEventListener('focus', surFocus);
    return () => window.removeEventListener('focus', surFocus);
  }, [connecte, rafraichirCompteur]);

  // Réinscription push si déjà accordée + connecté. AUSSI au retour de focus : un
  // appareil qui s'est abonné DÉCONNECTÉ (rangé sous pseudonyme) re-clé alors son
  // abonnement sous l'id de membre dès qu'il revient au premier plan connecté ->
  // toutes les sessions connectées convergent vers le bon ciblage (une mention les
  // touche toutes). Idempotent (upsert par endpoint), donc sûr à répéter.
  useEffect(() => {
    if (!connecte || !notificationsSupportees()) return;
    const reKey = () => {
      if (Notification.permission === 'granted') void activerNotifications().catch(() => {});
    };
    reKey();
    window.addEventListener('focus', reKey);
    return () => window.removeEventListener('focus', reKey);
  }, [connecte]);

  const activerPush = () => {
    setInfo(null);
    if (!notificationsSupportees()) {
      setInfo("Pour les notifications push, installe d'abord OcéRM sur ton écran d'accueil.");
      return;
    }
    if (!estConnecte()) {
      setInfo('Connecte-toi d’abord pour activer les notifications push.');
      return;
    }
    void activerNotifications()
      .then((ok) => {
        setEtat(etatAutorisation());
        setInfo(
          ok
            ? 'Notifications push activées sur cet appareil.'
            : Notification.permission === 'denied'
              ? 'Notifications refusées dans les réglages du téléphone.'
              : "Installe OcéRM sur ton écran d'accueil pour activer les notifications push.",
        );
      })
      .catch((e: unknown) =>
        setInfo(`Échec activation push (${e instanceof Error ? e.message : 'inconnu'}).`),
      );
  };

  // Push de test ciblé sur CET appareil (via son endpoint) : distingue 3 cas ->
  // (a) appareil non abonné sous le membre, (b) abonné + livré (si rien à l'écran =
  // réglage OS/navigateur), (c) rien d'abonné.
  const testerPush = () => {
    setInfo('Envoi du test…');
    void endpointPushActuel()
      .then((endpoint) => testerPushMoi(endpoint))
      .then(({ envoyees, surCetAppareil }) => {
        setInfo(
          surCetAppareil === false
            ? 'Cet appareil n’est PAS abonné sous ton compte. Clique « Activer les push » ici (bien connecté), puis réessaie.'
            : envoyees > 0
              ? 'Test envoyé à cet appareil ✓. Si rien n’apparaît : autorise les notifications du navigateur dans les réglages de l’appareil (iPhone : OcéRM doit être installé sur l’écran d’accueil).'
              : 'Cet appareil est abonné mais l’envoi a échoué (abonnement expiré ou erreur push). Clique « Activer les push » pour le réabonner, puis réessaie.',
        );
      })
      .catch((e: unknown) =>
        setInfo(`Échec du test (${e instanceof Error ? e.message : 'inconnu'}).`),
      );
  };

  const ouvrirNotification = async (n: NotificationOcerm) => {
    setActionId(n.id);
    setInfo(null);
    try {
      const r = await marquerNotificationLue(n.id);
      setNonLues(r.nonLues);
      setNotifications((xs) => xs.map((x) => (x.id === n.id ? { ...x, lu: true } : x)));
      const contactId = contactIdDepuisLien(n.lien);
      if (contactId) {
        setOuvert(false);
        await onOuvrirContact(contactId);
      }
    } catch {
      setInfo('Action impossible. Réessaie.');
    } finally {
      setActionId(null);
    }
  };

  const toutMarquerLu = async () => {
    setActionId('tout');
    setInfo(null);
    try {
      const r = await marquerToutesNotificationsLues();
      setNonLues(r.nonLues);
      setNotifications((xs) => xs.map((x) => ({ ...x, lu: true })));
      setInfo(r.marquees > 0 ? `${r.marquees} notification(s) marquée(s) lues.` : 'Déjà à jour.');
    } catch {
      setInfo('Action impossible. Réessaie.');
    } finally {
      setActionId(null);
    }
  };

  const accordees = etat === 'granted';

  return (
    <span className="cloche-bloc">
      <button
        type="button"
        className={`${accordees ? 'cloche-on' : 'cloche'} cloche-bouton`}
        title="Notifications"
        aria-label={nonLues > 0 ? `${nonLues} notifications non lues` : 'Notifications'}
        aria-expanded={ouvert}
        onClick={() => {
          const prochain = !ouvert;
          setOuvert(prochain);
          setInfo(null);
          if (prochain) void charger();
        }}
      >
        {accordees ? <IconeCloche size={20} /> : <IconeClocheOff size={20} />}
        {nonLues > 0 && <span className="cloche-badge">{nonLues > 99 ? '99+' : nonLues}</span>}
      </button>

      {ouvert && (
        <div className="notifications-panneau" role="dialog" aria-label="Centre de notifications">
          <div className="notifications-entete">
            <strong>Notifications</strong>
            <button
              type="button"
              className="notifications-action"
              disabled={nonLues === 0 || actionId === 'tout'}
              onClick={() => void toutMarquerLu()}
            >
              Tout marquer lu
            </button>
          </div>

          <button type="button" className="notifications-push" onClick={activerPush}>
            {accordees ? <IconeCloche size={16} /> : <IconeClocheOff size={16} />}
            <span>{accordees ? 'Push activées' : 'Activer les push'}</span>
          </button>

          {accordees && (
            <button type="button" className="notifications-action" onClick={testerPush}>
              Tester la notification sur cet appareil
            </button>
          )}

          {info && (
            <p className="notifications-info" role="status">
              {info}
            </p>
          )}

          <div className="notifications-liste">
            {chargement && notifications.length === 0 ? (
              <p className="notifications-vide">Chargement...</p>
            ) : notifications.length === 0 ? (
              <p className="notifications-vide">Aucune notification.</p>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`notification-item ${n.lu ? 'notification-lue' : 'notification-non-lue'}`}
                  disabled={actionId === n.id}
                  onClick={() => void ouvrirNotification(n)}
                >
                  <span className={`notification-vignette notification-vignette-${n.type}`}>
                    {n.type === 'mention' ? '@' : 'N'}
                  </span>
                  <span className="notification-contenu">
                    <span className="notification-ligne">
                      <strong>{n.titre}</strong>
                      <time dateTime={n.createdAt}>{dateRelative(n.createdAt)}</time>
                    </span>
                    <span className="notification-corps">{extraitNotification(n.corps)}</span>
                  </span>
                  {!n.lu && <span className="notification-point" aria-hidden="true" />}
                </button>
              ))
            )}
          </div>

          {prochainAvant && (
            <button
              type="button"
              className="notifications-plus"
              disabled={chargement}
              onClick={() => void charger(prochainAvant)}
            >
              Voir plus
            </button>
          )}
        </div>
      )}
    </span>
  );
}
